import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import * as XLSX from 'xlsx';
import { translations } from '../translations';
import PatientHistoryModal from './PatientHistoryModal';

// Mappings des questions et réponses pour les questionnaires
const PROMIS_QUESTIONS = {
  'PFA11': 'Können Sie Hausarbeiten erledigen, z.B. Staubsaugen oder Gartenarbeit?',
  'PFA21': 'Können Sie mit normaler Geschwindigkeit Treppen hoch- und runtergehen?',
  'PFA23': 'Können Sie mindestens 15 Minuten lang spazieren gehen?',
  'PFA53': 'Können Sie Besorgungen machen und einkaufen gehen?',
  'EDANX01': 'Ich fürchtete mich.',
  'EDANX40': 'Ich fand es schwer, auf etwas anderes zu achten als auf meine Angst.',
  'EDANX41': 'Meine Sorgen haben mich überwältigt.',
  'EDANX53': 'Ich fühlte mich unruhig.',
  'EDDEP04': 'Ich fühlte mich wertlos.',
  'EDDEP06': 'Ich fühlte mich hilflos.',
  'EDDEP29': 'Ich fühlte mich niedergeschlagen.',
  'EDDEP41': 'Ich fühlte mich hoffnungslos.',
  'HI7': 'Ich bin erschöpft.',
  'AN3': 'Es fällt mir schwer, etwas anzufangen, weil ich müde bin.',
  'FATEXP41': 'Wie ausgelaugt fühlten Sie sich im Allgemeinen?',
  'FATEXP40': 'Wie erschöpft waren Sie im Allgemeinen?',
  'Sleep109': 'Meine Schlafqualität war ...',
  'Sleep116': 'Mein Schlaf war erfrischend.',
  'Sleep20': 'Ich hatte Schlafprobleme.',
  'Sleep44': 'Ich hatte Schwierigkeiten einzuschlafen.',
  'SRPPER11-CaPS': 'Es fällt mir schwer, allen meinen regelmäßigen Freizeitaktivitäten mit anderen nachzugehen.',
  'SRPPER18-CaPS': 'Es fällt mir schwer, allen Aktivitäten nachzugehen, die ich mit meiner Familie machen möchte.',
  'SRPPER23-CaPS': 'Es fällt mir schwer, meine gewohnte Arbeit zu erledigen (einschließlich der Arbeit zu Hause).',
  'SRPPER46-CaPS': 'Es fällt mir schwer, allen Aktivitäten nachzugehen, die ich mit Freunden machen möchte.',
  'PAININ9': 'Wie sehr beeinträchtigten Schmerzen Ihre Alltagsaktivitäten?',
  'PAININ22': 'Wie sehr beeinträchtigten Schmerzen Ihre häuslichen Tätigkeiten?',
  'PAININ31': 'Wie sehr beeinträchtigten Schmerzen Ihre Fähigkeit, an sozialen Aktivitäten teilzunehmen?',
  'PAININ34': 'Wie sehr beeinträchtigten Schmerzen Sie bei Ihrer Hausarbeit?',
  'Global07': 'Wie würden Sie Ihre Schmerzen im Allgemeinen einschätzen?',
  'PC2r': 'Ich war langsam beim Denken',
  'PC35r': 'Es kam mir so vor, als ob mein Gehirn nicht so gut wie sonst funktionierte',
  'PC36r': 'Ich musste mir größere Mühe geben als sonst, um den Überblick über das zu bewahren, womit ich gerade beschäftigt war',
  'PC42r': 'Ich hatte Schwierigkeiten, zwischen verschiedenen Aktivitäten, die ein gewisses Nachdenken erfordern, hin und her zu wechseln.'
};

const PROMIS_ANSWERS = {
  // Physical function
  'PFA11': { 5: 'Ohne jede Schwierigkeiten', 4: 'Mit geringen Schwierigkeiten', 3: 'Mit einigen Schwierigkeiten', 2: 'Mit großen Schwierigkeiten', 1: 'Kann ich gar nicht' },
  'PFA21': { 5: 'Ohne jede Schwierigkeiten', 4: 'Mit geringen Schwierigkeiten', 3: 'Mit einigen Schwierigkeiten', 2: 'Mit großen Schwierigkeiten', 1: 'Kann ich gar nicht' },
  'PFA23': { 5: 'Ohne jede Schwierigkeiten', 4: 'Mit geringen Schwierigkeiten', 3: 'Mit einigen Schwierigkeiten', 2: 'Mit großen Schwierigkeiten', 1: 'Kann ich gar nicht' },
  'PFA53': { 5: 'Ohne jede Schwierigkeiten', 4: 'Mit geringen Schwierigkeiten', 3: 'Mit einigen Schwierigkeiten', 2: 'Mit großen Schwierigkeiten', 1: 'Kann ich gar nicht' },
  // Anxiety & Depression
  'EDANX01': { 1: 'Nie', 2: 'Selten', 3: 'Manchmal', 4: 'Oft', 5: 'Immer' },
  'EDANX40': { 1: 'Nie', 2: 'Selten', 3: 'Manchmal', 4: 'Oft', 5: 'Immer' },
  'EDANX41': { 1: 'Nie', 2: 'Selten', 3: 'Manchmal', 4: 'Oft', 5: 'Immer' },
  'EDANX53': { 1: 'Nie', 2: 'Selten', 3: 'Manchmal', 4: 'Oft', 5: 'Immer' },
  'EDDEP04': { 1: 'Nie', 2: 'Selten', 3: 'Manchmal', 4: 'Oft', 5: 'Immer' },
  'EDDEP06': { 1: 'Nie', 2: 'Selten', 3: 'Manchmal', 4: 'Oft', 5: 'Immer' },
  'EDDEP29': { 1: 'Nie', 2: 'Selten', 3: 'Manchmal', 4: 'Oft', 5: 'Immer' },
  'EDDEP41': { 1: 'Nie', 2: 'Selten', 3: 'Manchmal', 4: 'Oft', 5: 'Immer' },
  // Fatigue
  'HI7': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  'AN3': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  'FATEXP41': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  'FATEXP40': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  // Sleep
  'Sleep109': { 5: 'Sehr gut', 4: 'Gut', 3: 'Mäßig', 2: 'Schlecht', 1: 'Sehr schlecht' },
  'Sleep116': { 5: 'Überhaupt nicht', 4: 'Ein wenig', 3: 'Mäßig', 2: 'Ziemlich', 1: 'Sehr' },
  'Sleep20': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  'Sleep44': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  // Social
  'SRPPER11-CaPS': { 5: 'Nie', 4: 'Selten', 3: 'Manchmal', 2: 'Oft', 1: 'Immer' },
  'SRPPER18-CaPS': { 5: 'Nie', 4: 'Selten', 3: 'Manchmal', 2: 'Oft', 1: 'Immer' },
  'SRPPER23-CaPS': { 5: 'Nie', 4: 'Selten', 3: 'Manchmal', 2: 'Oft', 1: 'Immer' },
  'SRPPER46-CaPS': { 5: 'Nie', 4: 'Selten', 3: 'Manchmal', 2: 'Oft', 1: 'Immer' },
  // Pain
  'PAININ9': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  'PAININ22': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  'PAININ31': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  'PAININ34': { 1: 'Überhaupt nicht', 2: 'Ein wenig', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
  // Cognitive
  'PC2r': { 5: 'Nie', 4: 'Selten (einmal)', 3: 'Manchmal (zwei- oder dreimal)', 2: 'Oft (ungefähr einmal täglich)', 1: 'Sehr oft (mehrmals täglich)' },
  'PC35r': { 5: 'Nie', 4: 'Selten (einmal)', 3: 'Manchmal (zwei- oder dreimal)', 2: 'Oft (ungefähr einmal täglich)', 1: 'Sehr oft (mehrmals täglich)' },
  'PC36r': { 5: 'Nie', 4: 'Selten (einmal)', 3: 'Manchmal (zwei- oder dreimal)', 2: 'Oft (ungefähr einmal täglich)', 1: 'Sehr oft (mehrmals täglich)' },
  'PC42r': { 5: 'Nie', 4: 'Selten (einmal)', 3: 'Manchmal (zwei- oder dreimal)', 2: 'Oft (ungefähr einmal täglich)', 1: 'Sehr oft (mehrmals täglich)' }
};

const INFECTION_QUESTIONS = {
  'fever': '1. Hatten Sie in den letzten 7 Tagen Fieber über 38°C?',
  'hip_inflammation': '2. War Ihre Hüfte (Operationsbereich) wärmer, röter oder geschwollen?',
  'increased_pain': '3. Hatten Sie neu aufgetretene Schmerzen in der operierten Hüfte, die stärker wurden?',
  'wound_drainage': '4. Gab es Flüssigkeitsaustritt/Wundsekretion aus der Operationsnarbe?',
  'medical_examination': '5. Mussten Sie sich in den letzten 7 Tagen erneut einer Untersuchung oder Behandlung wegen Infektionsverdachts unterziehen?',
  'medical_examination_details': '5. Details zur Untersuchung/Behandlung',
  'diarrhea': '6. Hatten Sie in den letzten 7 Tagen Durchfall (≥3 ungeformte Stühle pro Tag) für ≥2 Tage?',
  'respiratory_infection': '7. Hatten Sie in den letzten 7 Tagen einen Atemwegsinfekt (Husten, Halsschmerzen, Kurzatmigkeit)?',
  'urinary_infection': '8. Hatten Sie in den letzten 7 Tagen einen Harnwegsinfekt (Brennen beim Wasserlassen, häufiges Wasserlassen)?',
  'antibiotics': '9. Haben Sie in den letzten 7 Tagen Antibiotika eingenommen?',
  'antibiotics_details': '9. Details zu Antibiotika',
  'ppi': '10. Haben Sie in den letzten 7 Tagen Protonenpumpenhemmer (PPI) eingenommen? (z. B. Omeprazol, Pantoprazol, Esomeprazol)',
  'ppi_details': '10. Details zu PPI',
  'probiotics': '11. Haben Sie in den letzten 7 Tagen Probiotika oder Prä-/Synbiotika eingenommen?',
  'probiotics_details': '11. Details zu Probiotika',
  'hospital_stay': '12. Gab es in den letzten 7 Tagen Krankenhausaufenthalte?',
  'hospital_stay_reason': '12. Grund für Krankenhausaufenthalt'
};

const INFECTION_ANSWERS = {
  'yes': 'Ja',
  'no': 'Nein',
  true: 'Ja',
  false: 'Nein',
  'true': 'Ja',
  'false': 'Nein'
};

// Placeholders pour HOOS (utiliseront des fallbacks si non renseignés)
const HOOS_QUESTIONS = {
  // Symptome
  'HOOS_SYM1': 'Spüren Sie ein Reiben, hören Sie ein Klicken, Knirschen, Knacken oder ein anderes Geräusch in der Hüfte?',
  'HOOS_SYM2': 'Haben Sie Schwierigkeiten, die Beine zu spreizen?',
  'HOOS_SYM3': 'Haben Sie Schwierigkeiten, beim Gehen große Schritte zu machen?',
  'HOOS_SYM4': 'Wie ausgeprägt ist die Steifigkeit in Ihrem Hüftgelenk wenn Sie morgens aufwachen?',
  'HOOS_SYM5': 'Wie ausgeprägt ist die Steifigkeit in Ihrem Hüftgelenk nach dem Sitzen, Liegen oder Ausruhen im Verlauf des Tages?',
  // Schmerz
  'HOOS_PAIN1': 'Wie oft haben Sie Schmerzen in der Hüfte?',
  'HOOS_PAIN2': 'Vollständiges Strecken der Hüfte (Aufrichten)?',
  'HOOS_PAIN3': 'Maximales Beugen der Hüfte?',
  'HOOS_PAIN4': 'Gehen in unebenem Gelände?',
  'HOOS_PAIN5': 'Treppen hinauf- oder hinuntersteigen?',
  'HOOS_PAIN6': 'Nachts im Bett?',
  'HOOS_PAIN7': 'Sitzen oder Liegen?',
  'HOOS_PAIN8': 'Aufrecht stehen?',
  'HOOS_PAIN9': 'Gehen auf hartem Boden?',
  // ADL
  'HOOS_ADL1': 'Treppen hinuntersteigen?',
  'HOOS_ADL2': 'Treppen hinaufsteigen?',
  'HOOS_ADL3': 'Vom Sitzen aufstehen?',
  'HOOS_ADL4': 'Stehen?',
  'HOOS_ADL5': 'Sich bücken/einen Gegenstand vom Boden aufheben?',
  'HOOS_ADL6': 'Gehen in unebenem Gelände?',
  'HOOS_ADL7': 'Einsteigen in ein Auto/Aussteigen aus einem Auto?',
  'HOOS_ADL8': 'Einkaufen?',
  'HOOS_ADL9': 'Socken/Strümpfe anziehen?',
  'HOOS_ADL10': 'Vom Bett aufstehen?',
  'HOOS_ADL11': 'Socken/Strümpfe ausziehen?',
  'HOOS_ADL12': 'Im Bett liegen (Drehen mit Beibehalten der Hüftposition)?',
  'HOOS_ADL13': 'Einsteigen in die Badewanne/Aussteigen aus der Badewanne?',
  'HOOS_ADL14': 'Sitzen?',
  'HOOS_ADL15': 'Sich auf die Toilette hinsetzen und wieder aufstehen?',
  'HOOS_ADL16': 'Schwere Hausarbeit verrichten (Kisten tragen, Boden wischen…)?',
  'HOOS_ADL17': 'Leichte Hausarbeit verrichten (Kochen, Staub wischen…)?',
  // Sport/Freizeit
  'HOOS_SPORT1': 'Kauern/in die Hocke gehen?',
  'HOOS_SPORT2': 'Rennen?',
  'HOOS_SPORT3': 'Drehen auf dem belasteten Bein?',
  'HOOS_SPORT4': 'Gehen in unebenem Gelände?',
  // Lebensqualität
  'HOOS_QOL1': 'Wie oft sind Sie sich Ihres Hüftproblems bewusst?',
  'HOOS_QOL2': 'Haben Sie Ihren Lebensstil verändert, um Tätigkeiten zu vermeiden, die Ihrer Hüfte schaden könnten?',
  'HOOS_QOL3': 'Wie sehr belastet Sie Ihr fehlendes Vertrauen in Ihre Hüfte?',
  'HOOS_QOL4': 'Wie groß sind die Schwierigkeiten, die Sie im Allgemeinen mit Ihrer Hüfte haben?'
};

const HOOS_ANSWERS = (() => {
  const freq5A = { 0: 'nie', 1: 'selten', 2: 'manchmal', 3: 'oft', 4: 'immer' };
  const freq5B = { 0: 'nie', 1: 'monatlich', 2: 'wöchentlich', 3: 'täglich', 4: 'immer' };
  const freq5C = { 0: 'niemals', 1: 'selten', 2: 'manchmal', 3: 'oft', 4: 'immer' };
  const diff5A = { 0: 'keine', 1: 'wenig', 2: 'mäßige', 3: 'große', 4: 'sehr große' };
  const diffStrong = { 0: 'keine', 1: 'wenig', 2: 'mäßige', 3: 'starke', 4: 'sehr starke' };
  const intensity5A = { 0: 'gar nicht', 1: 'wenig', 2: 'mäßig', 3: 'stark', 4: 'sehr stark' };
  const diffEinige = { 0: 'keine', 1: 'wenig', 2: 'einige', 3: 'große', 4: 'sehr große' };
  const map = {};
  // SYM
  map['HOOS_SYM1'] = freq5A;
  map['HOOS_SYM2'] = diff5A;
  map['HOOS_SYM3'] = diff5A;
  map['HOOS_SYM4'] = intensity5A;
  map['HOOS_SYM5'] = freq5C;
  // PAIN
  map['HOOS_PAIN1'] = freq5B;
  ['HOOS_PAIN2','HOOS_PAIN3','HOOS_PAIN4','HOOS_PAIN5','HOOS_PAIN6','HOOS_PAIN7','HOOS_PAIN8','HOOS_PAIN9'].forEach(k => map[k] = diffStrong);
  // ADL
  ['HOOS_ADL1','HOOS_ADL2','HOOS_ADL3','HOOS_ADL4','HOOS_ADL5','HOOS_ADL6','HOOS_ADL7','HOOS_ADL8','HOOS_ADL9','HOOS_ADL10','HOOS_ADL11','HOOS_ADL13','HOOS_ADL14','HOOS_ADL15','HOOS_ADL16','HOOS_ADL17'].forEach(k => map[k] = diff5A);
  map['HOOS_ADL12'] = diffEinige;
  // SPORT
  ['HOOS_SPORT1','HOOS_SPORT2','HOOS_SPORT3','HOOS_SPORT4'].forEach(k => map[k] = diff5A);
  // QOL
  map['HOOS_QOL1'] = freq5B;
  map['HOOS_QOL2'] = intensity5A;
  map['HOOS_QOL3'] = intensity5A;
  map['HOOS_QOL4'] = diff5A;
  return map;
})();

function AdminDashboard({ onLogout, language = 'en' }) {
  const [patients, setPatients] = useState([]);
  const [editingPatient, setEditingPatient] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPatient, setNewPatient] = useState({ patientId: '', password: '', startDate: '', group: 'Fasting' });
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedPatientForHistory, setSelectedPatientForHistory] = useState(null);
  const [showExcelPreview, setShowExcelPreview] = useState(false);
  const [excelPreviewData, setExcelPreviewData] = useState({
    summary: [],
    detailed: [],
    degs: [],
    infection: [],
    promis: [],
    hoos: []
  });
  const [activeTab, setActiveTab] = useState('summary');
  const [filters, setFilters] = useState({
    summary: {},
    detailed: {},
    ohs: {},
    sf36: {},
    qor15: {},
    degs: {},
    infection: {},
    promis: {},
    hoos: {}
  });

  const t = translations[language];

  // Fonctions de filtrage
  const handleFilterChange = (tab, columnIndex, value) => {
    setFilters(prev => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        [columnIndex]: value
      }
    }));
  };

  const filterData = (data, tab) => {
    if (!data || data.length === 0) return data;
    
    const tabFilters = filters[tab];
    if (!tabFilters || Object.keys(tabFilters).length === 0) return data;
    
    return data.filter((row, rowIndex) => {
      // Ignorer la première ligne (en-têtes)
      if (rowIndex === 0) return true;
      
      return Object.entries(tabFilters).every(([columnIndex, filterValue]) => {
        if (!filterValue) return true;
        
        const cellValue = row[parseInt(columnIndex)];
        if (cellValue === null || cellValue === undefined) return false;
        
        return cellValue.toString().toLowerCase().includes(filterValue.toLowerCase());
      });
    });
  };

  const clearFilters = (tab) => {
    setFilters(prev => ({
      ...prev,
      [tab]: {}
    }));
  };

  // Calculer le niveau de retard et la couleur
  const getDelayLevel = (patient) => {
    if (!patient.overallProgress || !patient.expectedProgress) return 0;
    
    const delay = patient.expectedProgress - patient.overallProgress;
    console.log(`Patient ${patient.patientId}: overall=${patient.overallProgress}%, expected=${patient.expectedProgress}%, delay=${delay}%`);
    
    if (delay <= 0) return 0; // Pas de retard
    if (delay <= 20) return 1; // Retard léger
    if (delay <= 40) return 2; // Retard modéré
    if (delay <= 60) return 3; // Retard important
    if (delay <= 80) return 4; // Retard sévère
    return 5; // Retard critique
  };

  const getDelayColor = (level) => {
    // Couleur verte pour la jauge réelle (style valide/ok)
    const color = '#10b981'; // Vert émeraude
    console.log(`Delay level ${level} -> color ${color} (vert valide)`);
    return color;
  };

  const getExpectedColor = (patient) => {
    // Gérer les cas où les valeurs sont 0 ou undefined
    const overall = patient.overallProgress || 0;
    const expected = patient.expectedProgress || 0;
    
    console.log(`Patient ${patient.patientId}: overall=${overall}%, expected=${expected}%`);
    
    const delay = expected - overall;
    console.log(`Patient ${patient.patientId}: delay=${delay}%`);
    
    // Couleurs variables selon l'amplitude de l'écart
    if (delay <= 0) return '#10b981'; // Vert - pas de retard
    if (delay < 5) return '#fef3c7'; // Light yellow - < 5%
    if (delay < 10) return '#fed7aa'; // Light orange-yellow (mixed) - 5-10%
    if (delay <= 15) return '#fdba74'; // Light orange - 10-15%
    if (delay <= 20) return '#fca5a5'; // Light red - 15-20%
    return '#ef4444'; // Rouge normal - > 20%
  };

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTimelineStats();
      if (response.success) {
        console.log('Données patients reçues:', response.stats);
        // Log détaillé pour chaque patient
        response.stats.forEach(patient => {
          console.log(`Patient ${patient.patientId}: overall=${patient.overallProgress}%, expected=${patient.expectedProgress}%, totalTasks=${patient.totalTasks}, completedTasks=${patient.completedTasks}`);
        });
        setPatients(response.stats);
      } else {
        setError(response.message || 'Erreur lors du chargement des patients');
      }
    } catch (error) {
      console.error('Erreur chargement patients:', error);
      setError(error.message || 'Erreur lors du chargement des patients');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPatient = (patient) => {
    setEditingPatient({ ...patient });
  };

  const handleSaveEdit = async () => {
    if (editingPatient) {
      try {
        const response = await apiService.updatePatient(editingPatient.patientId, {
          startDate: editingPatient.startDate,
          group: editingPatient.group
        });
        
        if (response.success) {
          await loadPatients();
          setEditingPatient(null);
          setError('');
        } else {
          setError(response.message || (language === 'de' ? 'Fehler beim Speichern' : 'Error saving patient'));
        }
      } catch (error) {
        console.error('Erreur mise à jour patient:', error);
        setError(error.message || (language === 'de' ? 'Fehler beim Speichern' : 'Error saving patient'));
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingPatient(null);
    setError('');
  };

  const handleDeletePatient = async (patientId) => {
    if (window.confirm(language === 'de' ? 'Patient wirklich löschen?' : 'Really delete this patient?')) {
      try {
        const response = await apiService.deletePatient(patientId);
        if (response.success) {
          await loadPatients();
          setError('');
        } else {
          setError(response.message || (language === 'de' ? 'Fehler beim Löschen' : 'Error deleting patient'));
        }
      } catch (error) {
        console.error('Erreur suppression patient:', error);
        setError(error.message || (language === 'de' ? 'Fehler beim Löschen' : 'Error deleting patient'));
      }
    }
  };

  const handleAddPatient = async () => {
    console.log('Données du nouveau patient:', newPatient);
    
    if (!newPatient.patientId || !newPatient.patientId.trim()) {
      setError('Patient ID est requis');
      return;
    }
    
    if (!newPatient.password || !newPatient.password.trim()) {
      setError('Mot de passe est requis');
      return;
    }
    
    if (!newPatient.startDate) {
      setError('Date de début est requise');
      return;
    }

    const allowedGroups = ['Control','Fasting'];
    const payload = {
      patientId: newPatient.patientId,
      password: newPatient.password,
      startDate: newPatient.startDate,
      group: allowedGroups.includes(newPatient.group) ? newPatient.group : 'Control'
    };

    try {
      console.log('Envoi des données au backend:', payload);
      const response = await apiService.createPatient(payload);
      console.log('Réponse du backend:', response);
      
      if (response.success) {
        await loadPatients();
        setNewPatient({ patientId: '', password: '', startDate: '', group: 'Fasting' });
        setShowAddForm(false);
        setError('');
      } else {
        setError(response.message || (language === 'de' ? 'Fehler beim Hinzufügen' : 'Error adding patient'));
      }
    } catch (error) {
      console.error('Erreur ajout patient:', error);
      if (error.message.includes('already exists')) {
        setError(language === 'de' ? 'Patient ID existiert bereits' : 'Patient ID already exists');
      } else {
        setError(error.message || (language === 'de' ? 'Fehler beim Hinzufügen' : 'Error adding patient'));
      }
    }
  };

  const generateExcelPreviewData = async () => {
    try {
      setLoading(true);
      
      // Définitions des mappings et configurations (même que dans handleExportExcel)
      const questionIdMapping = {
        // OHS mappings
        body_side: 'Q0',
        pain: 'Q1',
        night_pain: 'Q2',
        sudden_pain: 'Q3',
        limping: 'Q4',
        walking_duration: 'Q5',
        stairs: 'Q6',
        socks: 'Q7',
        standing_up: 'Q8',
        transport: 'Q9',
        washing: 'Q10',
        shopping: 'Q11',
        work_interference: 'Q12',
        // SF36 mappings
        gh1: 'GH1',
        gh2: 'GH2',
        gh3: 'GH3',
        gh4: 'GH4',
        gh5: 'GH5',
        gh6: 'GH6',
        gh7: 'GH7',
        pf1: 'PF1',
        pf2: 'PF2',
        pf3: 'PF3',
        pf4: 'PF4',
        pf5: 'PF5',
        pf6: 'PF6',
        pf7: 'PF7',
        pf8: 'PF8',
        pf9: 'PF9',
        pf10: 'PF10',
        rp11: 'RP11',
        rp12: 'RP12',
        rp13: 'RP13',
        rp14: 'RP14',
        re11: 'RE11',
        re12: 'RE12',
        re13: 'RE13',
        sf1: 'SF1',
        sf2: 'SF2',
        bp1: 'BP1',
        bp2: 'BP2',
        mh11: 'MH11',
        mh12: 'MH12',
        mh13: 'MH13',
        mh14: 'MH14',
        mh15: 'MH15',
        mh16: 'MH16',
        mh17: 'MH17',
        mh18: 'MH18',
        mh19: 'MH19',
        mh110: 'MH110',
        mh111: 'MH111',
        mh112: 'MH112',
        mh113: 'MH113',
        mh114: 'MH114',
        mh115: 'MH115',
        mh116: 'MH116',
        mh117: 'MH117',
        mh118: 'MH118',
        mh119: 'MH119',
        mh120: 'MH120',
        mh121: 'MH121',
        mh122: 'MH122',
        mh123: 'MH123',
        mh124: 'MH124',
        mh125: 'MH125',
        mh126: 'MH126',
        mh127: 'MH127',
        mh128: 'MH128',
        mh129: 'MH129',
        mh130: 'MH130',
        mh131: 'MH131',
        mh132: 'MH132',
        mh133: 'MH133',
        mh134: 'MH134',
        mh135: 'MH135',
        mh136: 'MH136',
        mh137: 'MH137',
        mh138: 'MH138'
      };

      // Questions OHS avec leurs labels (version officielle validée)
      const ohsQuestions = {
        en: {
          body_side: 'On which side of your body is the affected joint for which you are receiving treatment?',
          pain: 'How would you describe the pain you usually have in your hip?',
          night_pain: 'Have you been troubled by pain from your hip in bed at night?',
          sudden_pain: 'Have you had any sudden, severe pain (shooting, stabbing, or spasms) from your affected hip?',
          limping: 'Have you been limping when walking because of your hip?',
          walking_duration: 'For how long have you been able to walk before the pain in your hip becomes severe (with or without a walking aid)?',
          stairs: 'Have you been able to climb a flight of stairs?',
          socks: 'Have you been able to put on a pair of socks, stockings or tights?',
          standing_up: 'After a meal (sat at a table), how painful has it been for you to stand up from a chair because of your hip?',
          transport: 'Have you had any trouble getting in and out of a car or using public transportation because of your hip?',
          washing: 'Have you had any trouble with washing and drying yourself (all over) because of your hip?',
          shopping: 'Could you do the household shopping on your own?',
          work_interference: 'How much has pain from your hip interfered with your usual work, including housework?'
        },
        de: {
          body_side: 'Auf welcher Körperseite befindet sich das betroffene Gelenk, für das Sie eine Behandlung erhalten?',
          pain: 'Wie würden Sie den Schmerz beschreiben, den Sie normalerweise von Ihrer Hüfte haben?',
          night_pain: 'Wurden Sie nachts im Bett durch Schmerzen von Ihrer Hüfte gestört?',
          sudden_pain: 'Hatten Sie plötzliche, starke Schmerzen (stechende, stichartige oder krampfartige) von Ihrer betroffenen Hüfte?',
          limping: 'Hinken Sie beim Gehen wegen Ihrer Hüfte?',
          walking_duration: 'Wie lange konnten Sie laufen, bevor der Schmerz von Ihrer Hüfte stark wird (mit oder ohne Gehhilfe)?',
          stairs: 'Konnten Sie eine Treppe hinaufsteigen?',
          socks: 'Konnten Sie ein Paar Socken, Strümpfe oder Strumpfhosen anziehen?',
          standing_up: 'Nach einer Mahlzeit (am Tisch gesessen), wie schmerzhaft war es für Sie, von einem Stuhl aufzustehen wegen Ihrer Hüfte?',
          transport: 'Hatten Sie Probleme beim Ein- und Aussteigen aus einem Auto oder bei der Nutzung öffentlicher Verkehrsmittel wegen Ihrer Hüfte?',
          washing: 'Hatten Sie Probleme beim Waschen und Trocknen (am ganzen Körper) wegen Ihrer Hüfte?',
          shopping: 'Konnten Sie alleine einkaufen gehen?',
          work_interference: 'Wie sehr haben Schmerzen von Ihrer Hüfte Ihre übliche Arbeit, einschließlich Hausarbeit, beeinträchtigt?'
        }
      };

      // Options de réponses OHS (version officielle)
      const ohsOptions = {
        en: {
          body_side: {
            1: 'Right', 2: 'Left', 3: 'Both'
          },
          pain: {
            1: 'None', 2: 'Very mild', 3: 'Mild', 4: 'Moderate', 5: 'Severe'
          },
          night_pain: {
            1: 'No nights', 2: 'Only 1 or 2 nights', 3: 'Some nights', 4: 'Most nights', 5: 'Every night'
          },
          sudden_pain: {
            1: 'No days', 2: 'Only 1 or 2 days', 3: 'Some days', 4: 'Most days', 5: 'Every day'
          },
          limping: {
            1: 'Rarely/never', 2: 'Sometimes or just at first', 3: 'Often, not just at first', 4: 'Most of the time', 5: 'All of the time'
          },
          walking_duration: {
            1: 'No pain for 30 minutes or more', 2: '16 to 30 minutes', 3: '5 to 15 minutes', 4: 'Around the house only', 5: 'Not at all'
          },
          stairs: {
            1: 'Yes, easily', 2: 'With little difficulty', 3: 'With moderate difficulty', 4: 'With extreme difficulty', 5: 'No, impossible'
          },
          socks: {
            1: 'Yes, easily', 2: 'With little difficulty', 3: 'With moderate difficulty', 4: 'With extreme difficulty', 5: 'No, impossible'
          },
          standing_up: {
            1: 'Not at all painful', 2: 'Slightly painful', 3: 'Moderately painful', 4: 'Very painful', 5: 'Unbearable'
          },
          transport: {
            1: 'No trouble at all', 2: 'Very little trouble', 3: 'Moderate trouble', 4: 'Extreme difficulty', 5: 'Impossible to do'
          },
          washing: {
            1: 'No trouble at all', 2: 'Very little trouble', 3: 'Moderate trouble', 4: 'Extreme difficulty', 5: 'Impossible to do'
          },
          shopping: {
            1: 'Yes, easily', 2: 'With little difficulty', 3: 'With moderate difficulty', 4: 'With extreme difficulty', 5: 'No, impossible'
          },
          work_interference: {
            1: 'Not at all', 2: 'A little bit', 3: 'Moderately', 4: 'Greatly', 5: 'Totally'
          }
        },
        de: {
          body_side: {
            1: 'Rechts', 2: 'Links', 3: 'Beide'
          },
          pain: {
            1: 'Keine', 2: 'Sehr mild', 3: 'Mild', 4: 'Moderat', 5: 'Schwer'
          },
          night_pain: {
            1: 'Keine Nächte', 2: 'Nur 1 oder 2 Nächte', 3: 'Einige Nächte', 4: 'Die meisten Nächte', 5: 'Jede Nacht'
          },
          sudden_pain: {
            1: 'Keine Tage', 2: 'Nur 1 oder 2 Tage', 3: 'Einige Tage', 4: 'Die meisten Tage', 5: 'Jeden Tag'
          },
          limping: {
            1: 'Selten/nie', 2: 'Manchmal oder nur am Anfang', 3: 'Oft, nicht nur am Anfang', 4: 'Die meiste Zeit', 5: 'Die ganze Zeit'
          },
          walking_duration: {
            1: 'Keine Schmerzen für 30 Minuten oder mehr', 2: '16 bis 30 Minuten', 3: '5 bis 15 Minuten', 4: 'Nur im Haus', 5: 'Überhaupt nicht'
          },
          stairs: {
            1: 'Ja, leicht', 2: 'Mit wenig Schwierigkeit', 3: 'Mit mäßiger Schwierigkeit', 4: 'Mit extremer Schwierigkeit', 5: 'Nein, unmöglich'
          },
          socks: {
            1: 'Ja, leicht', 2: 'Mit wenig Schwierigkeit', 3: 'Mit mäßiger Schwierigkeit', 4: 'Mit extremer Schwierigkeit', 5: 'Nein, unmöglich'
          },
          standing_up: {
            1: 'Überhaupt nicht schmerzhaft', 2: 'Leicht schmerzhaft', 3: 'Mäßig schmerzhaft', 4: 'Sehr schmerzhaft', 5: 'Unerträglich'
          },
          transport: {
            1: 'Überhaupt keine Probleme', 2: 'Sehr wenig Probleme', 3: 'Mäßige Probleme', 4: 'Extreme Schwierigkeit', 5: 'Unmöglich zu tun'
          },
          washing: {
            1: 'Überhaupt keine Probleme', 2: 'Sehr wenig Probleme', 3: 'Mäßige Probleme', 4: 'Extreme Schwierigkeit', 5: 'Unmöglich zu tun'
          },
          shopping: {
            1: 'Ja, leicht', 2: 'Mit wenig Schwierigkeit', 3: 'Mit mäßiger Schwierigkeit', 4: 'Mit extremer Schwierigkeit', 5: 'Nein, unmöglich'
          },
          work_interference: {
            1: 'Überhaupt nicht', 2: 'Ein bisschen', 3: 'Mäßig', 4: 'Sehr', 5: 'Vollständig'
          }
        }
      };

              // Questions SF-36 avec leurs labels en allemand (complètes)
        const sf36Questions = {
          de: {
            // Q1-Q2: Questions générales sur la santé
            gh1: 'Wie würden Sie Ihren Gesundheitszustand im Allgemeinen beschreiben?',
            gh2: 'Im Vergleich zum vergangenen Jahr, wie würden Sie Ihren derzeitigen Gesundheitszustand beschreiben?',
            
            // Q3: Physical Functioning (10 sous-questions)
            pf1: 'a. anstrengende Tätigkeiten, z.B. schnell laufen, schwere Gegenstände heben, anstrengenden Sport treiben',
            pf2: 'b. mittelschwere Tätigkeiten, z.B. einen Tisch verschieben, staubsaugen, kegeln, Golf spielen',
            pf3: 'c. Einkaufstaschen heben oder tragen',
            pf4: 'd. mehrere Treppenabsätze steigen',
            pf5: 'e. einen Treppenabsatz steigen',
            pf6: 'f. sich beugen, knien, bücken',
            pf7: 'g. mehr als 1 Kilometer zu Fuß gehen',
            pf8: 'h. mehrere Straßenkreuzungen weit zu Fuß gehen',
            pf9: 'i. eine Straßenkreuzung weit zu Fuß gehen',
            pf10: 'j. sich baden oder anziehen',
            
            // Q4: Role Physical (4 sous-questions)
            rp11: 'a. Ich konnte nicht so lange wie üblich tätig sein',
            rp12: 'b. Ich habe weniger geschafft als ich wollte',
            rp13: 'c. Ich konnte nur bestimmte Dinge tun',
            rp14: 'd. Ich hatte Schwierigkeiten bei der Ausführung (z.B. ich musste mich besonders anstrengen)',
            
            // Q5: Role Emotional (3 sous-questions)
            re11: 'a. Ich konnte nicht so lange wie üblich tätig sein',
            re12: 'b. Ich habe weniger geschafft als ich wollte',
            re13: 'c. Ich konnte nicht so sorgfältig wie üblich arbeiten',
            
            // Q6: Social Functioning
            sf1: 'Wie sehr haben Ihre körperliche Gesundheit oder seelischen Probleme in den vergangenen 4 Wochen Ihre normalen Kontakte zu Familienangehörigen, Freunden, Nachbarn oder zum Bekanntenkreis beeinträchtigt?',
            
            // Q7: Bodily Pain
            bp1: 'Wie stark waren Ihre Schmerzen in den vergangenen 4 Wochen?',
            
            // Q8: Bodily Pain (interférence)
            bp2: 'Inwieweit haben die Schmerzen Sie in den vergangenen 4 Wochen bei der Ausübung Ihrer Alltagstätigkeiten zu Hause und im Beruf behindert?',
            
            // Q9: Social Functioning (fréquence)
            sf2: 'Wie häufig haben Ihre körperliche Gesundheit oder seelischen Probleme in den vergangenen 4 Wochen Ihre Kontakte zu anderen Menschen (Besuche bei Freunden, Verwandten usw.) beeinträchtigt?',
            
            // Q10: General Health (4 sous-questions)
            gh3: 'a. Ich scheine etwas leichter als andere krank zu werden',
            gh4: 'b. Ich bin genauso gesund wie alle anderen, die ich kenne',
            gh5: 'c. Ich erwarte, daß meine Gesundheit nachläßt',
            gh6: 'd. Ich erfreue mich ausgezeichneter Gesundheit',
            
            // Q11: General Health (évaluation actuelle)
            gh7: 'Wie würden Sie Ihren derzeitigen Gesundheitszustand beschreiben?',
            
            // Q12: Mental Health (38 sous-questions)
            mh11: 'a. Ich bin andauernd müde',
            mh12: 'b. Ich habe nachts Schmerzen',
            mh13: 'c. Ich fühle mich niedergeschlagen',
            mh14: 'd. Ich habe unerträgliche Schmerzen',
            mh15: 'e. Ich nehme Tabletten, um schlafen zu können',
            mh16: 'f. Ich habe vergessen, wie es ist Freude zu empfinden',
            mh17: 'g. Ich fühle mich gereizt',
            mh18: 'h. Ich finde es schmerzhaft, meine Körperposition zu verändern',
            mh19: 'i. Ich fühle mich einsam',
            mh110: 'j. Ich kann mich nur innerhalb des Hauses bewegen',
            mh111: 'k. Es fällt mir schwer mich zu bücken',
            mh112: 'l. Alles strengt mich an',
            mh113: 'm. Ich wache in den frühen Morgenstunden auf',
            mh114: 'n. Ich kann überhaupt nicht gehen',
            mh115: 'o. Es fällt mir schwer, zu anderen Menschen Kontakt aufzunehmen',
            mh116: 'p. Die Tage ziehen sich',
            mh117: 'q. Ich habe Schwierigkeiten Treppen hinauf- und hinunterzugehen',
            mh118: 'r. Es fällt mir schwer nach Gegenständen zu greifen',
            mh119: 's. Ich habe Schmerzen beim Gehen',
            mh120: 't. Mir reißt derzeit oft der Geduldsfaden',
            mh121: 'u. Ich fühle, daß ich niemanden nahestehe',
            mh122: 'v. Ich liege nachts die meiste Zeit wach',
            mh123: 'w. Ich habe das Gefühl, die Kontrolle zu verlieren',
            mh124: 'x. Ich habe Schmerzen, wenn ich stehe',
            mh125: 'y. Es fällt mir schwer mich selbst anzuziehen',
            mh126: 'z. Meine Energie läßt schnell nach',
            mh127: 'aa. Es fällt mir schwer lange zu stehen (z.B. am Spülbecken, an der Bushaltestelle)',
            mh128: 'ab. Ich habe andauernd Schmerzen',
            mh129: 'ac. Ich brauche lange zum Einschlafen',
            mh130: 'ad. Ich habe das Gefühl für andere Menschen eine Last zu sein',
            mh131: 'ae. Sorgen halten mich nachts wach',
            mh132: 'af. Ich fühle, daß das Leben nicht lebenswert ist',
            mh133: 'ag. Ich schlafe nachts schlecht',
            mh134: 'ah. Es fällt mir schwer mit anderen Menschen auszukommen',
            mh135: 'ai. Ich brauche Hilfe, wenn ich mich außer Haus bewegen will (Stock oder jemand, der mich stützt)',
            mh136: 'aj. Ich habe Schmerzen, wenn ich Treppen hinauf- und hinuntergehe',
            mh137: 'ak. Ich wache deprimiert auf',
            mh138: 'al. Ich habe Schmerzen, wenn ich sitze'
          },
          en: {
            // Questions en anglais (pour référence)
            gh1: 'How would you describe your general health?',
            gh2: 'Compared to one year ago, how would you rate your health in general now?',
            // ... autres questions en anglais
          }
        };

        // Options de réponses SF-36 en allemand (complètes)
        const sf36Options = {
          de: {
            // Q3: Physical Functioning (10 questions)
            pf1: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf2: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf3: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf4: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf5: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf6: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf7: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf8: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf9: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            pf10: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
            
            // Q4: Role Physical (4 questions)
            rp11: { 1: 'Ja', 2: 'Nein' },
            rp12: { 1: 'Ja', 2: 'Nein' },
            rp13: { 1: 'Ja', 2: 'Nein' },
            rp14: { 1: 'Ja', 2: 'Nein' },
            
            // Q5: Role Emotional (3 questions)
            re11: { 1: 'Ja', 2: 'Nein' },
            re12: { 1: 'Ja', 2: 'Nein' },
            re13: { 1: 'Ja', 2: 'Nein' },
            
            // Q6: Social Functioning
            sf1: { 1: 'Überhaupt nicht', 2: 'Etwas', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
            
            // Q7: Bodily Pain
            bp1: { 1: 'Ich hatte keine Schmerzen', 2: 'Sehr leicht', 3: 'Leicht', 4: 'Mäßig', 5: 'Stark', 6: 'Sehr stark' },
            
            // Q8: Bodily Pain (interférence)
            bp2: { 1: 'Überhaupt nicht', 2: 'Ein bißchen', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
            
            // Q9: Social Functioning (fréquence)
            sf2: { 1: 'Immer', 2: 'Meistens', 3: 'Manchmal', 4: 'Selten', 5: 'Nie' },
            
            // Q1-Q2, Q10-Q11: General Health
            gh1: { 1: 'Ausgezeichnet', 2: 'Sehr gut', 3: 'Gut', 4: 'Weniger gut', 5: 'Schlecht' },
            gh2: { 1: 'Derzeit viel besser als vor einem Jahr', 2: 'Derzeit etwas besser als vor einem Jahr', 3: 'Etwa so wie vor einem Jahr', 4: 'Derzeit etwas schlechter als vor einem Jahr', 5: 'Derzeit viel schlechter als vor einem Jahr' },
            gh3: { 1: 'Trifft ganz zu', 2: 'Trifft weitgehend zu', 3: 'Weiß nicht', 4: 'Trifft weitgehend nicht zu', 5: 'Trifft überhaupt nicht zu' },
            gh4: { 1: 'Trifft ganz zu', 2: 'Trifft weitgehend zu', 3: 'Weiß nicht', 4: 'Trifft weitgehend nicht zu', 5: 'Trifft überhaupt nicht zu' },
            gh5: { 1: 'Trifft ganz zu', 2: 'Trifft weitgehend zu', 3: 'Weiß nicht', 4: 'Trifft weitgehend nicht zu', 5: 'Trifft überhaupt nicht zu' },
            gh6: { 1: 'Trifft ganz zu', 2: 'Trifft weitgehend zu', 3: 'Weiß nicht', 4: 'Trifft weitgehend nicht zu', 5: 'Trifft überhaupt nicht zu' },
            gh7: { 1: 'sehr gut', 2: 'gut', 3: 'mittelmäßig', 4: 'schlecht', 5: 'sehr schlecht' },
            
            // Q12: Mental Health (38 questions)
            mh11: { 1: 'Ja', 2: 'Nein' },
            mh12: { 1: 'Ja', 2: 'Nein' },
            mh13: { 1: 'Ja', 2: 'Nein' },
            mh14: { 1: 'Ja', 2: 'Nein' },
            mh15: { 1: 'Ja', 2: 'Nein' },
            mh16: { 1: 'Ja', 2: 'Nein' },
            mh17: { 1: 'Ja', 2: 'Nein' },
            mh18: { 1: 'Ja', 2: 'Nein' },
            mh19: { 1: 'Ja', 2: 'Nein' },
            mh110: { 1: 'Ja', 2: 'Nein' },
            mh111: { 1: 'Ja', 2: 'Nein' },
            mh112: { 1: 'Ja', 2: 'Nein' },
            mh113: { 1: 'Ja', 2: 'Nein' },
            mh114: { 1: 'Ja', 2: 'Nein' },
            mh115: { 1: 'Ja', 2: 'Nein' },
            mh116: { 1: 'Ja', 2: 'Nein' },
            mh117: { 1: 'Ja', 2: 'Nein' },
            mh118: { 1: 'Ja', 2: 'Nein' },
            mh119: { 1: 'Ja', 2: 'Nein' },
            mh120: { 1: 'Ja', 2: 'Nein' },
            mh121: { 1: 'Ja', 2: 'Nein' },
            mh122: { 1: 'Ja', 2: 'Nein' },
            mh123: { 1: 'Ja', 2: 'Nein' },
            mh124: { 1: 'Ja', 2: 'Nein' },
            mh125: { 1: 'Ja', 2: 'Nein' },
            mh126: { 1: 'Ja', 2: 'Nein' },
            mh127: { 1: 'Ja', 2: 'Nein' },
            mh128: { 1: 'Ja', 2: 'Nein' },
            mh129: { 1: 'Ja', 2: 'Nein' },
            mh130: { 1: 'Ja', 2: 'Nein' },
            mh131: { 1: 'Ja', 2: 'Nein' },
            mh132: { 1: 'Ja', 2: 'Nein' },
            mh133: { 1: 'Ja', 2: 'Nein' },
            mh134: { 1: 'Ja', 2: 'Nein' },
            mh135: { 1: 'Ja', 2: 'Nein' },
            mh136: { 1: 'Ja', 2: 'Nein' },
            mh137: { 1: 'Ja', 2: 'Nein' },
            mh138: { 1: 'Ja', 2: 'Nein' }
          },
          en: {
            // Options en anglais (pour référence)
            gh1: { 1: 'Excellent', 2: 'Very good', 3: 'Good', 4: 'Fair', 5: 'Poor' },
            gh2: { 1: 'Much better now than one year ago', 2: 'Somewhat better now than one year ago', 3: 'About the same as one year ago', 4: 'Somewhat worse now than one year ago', 5: 'Much worse now than one year ago' }
          }
        };

        // Configuration des questions SF-36 (complète)
        const sf36QuestionConfig = {
          gh1: { number_of_options: 5, to_inverse: false },
          gh2: { number_of_options: 5, to_inverse: false },
          gh3: { number_of_options: 5, to_inverse: false },
          gh4: { number_of_options: 5, to_inverse: false },
          gh5: { number_of_options: 5, to_inverse: false },
          gh6: { number_of_options: 5, to_inverse: false },
          gh7: { number_of_options: 5, to_inverse: false },
          pf1: { number_of_options: 3, to_inverse: false },
          pf2: { number_of_options: 3, to_inverse: false },
          pf3: { number_of_options: 3, to_inverse: false },
          pf4: { number_of_options: 3, to_inverse: false },
          pf5: { number_of_options: 3, to_inverse: false },
          pf6: { number_of_options: 3, to_inverse: false },
          pf7: { number_of_options: 3, to_inverse: false },
          pf8: { number_of_options: 3, to_inverse: false },
          pf9: { number_of_options: 3, to_inverse: false },
          pf10: { number_of_options: 3, to_inverse: false },
          rp11: { number_of_options: 2, to_inverse: false },
          rp12: { number_of_options: 2, to_inverse: false },
          rp13: { number_of_options: 2, to_inverse: false },
          rp14: { number_of_options: 2, to_inverse: false },
          re11: { number_of_options: 2, to_inverse: false },
          re12: { number_of_options: 2, to_inverse: false },
          re13: { number_of_options: 2, to_inverse: false },
          sf1: { number_of_options: 5, to_inverse: false },
          sf2: { number_of_options: 5, to_inverse: false },
          bp1: { number_of_options: 6, to_inverse: false },
          bp2: { number_of_options: 5, to_inverse: false },
          mh11: { number_of_options: 2, to_inverse: false },
          mh12: { number_of_options: 2, to_inverse: false },
          mh13: { number_of_options: 2, to_inverse: false },
          mh14: { number_of_options: 2, to_inverse: false },
          mh15: { number_of_options: 2, to_inverse: false },
          mh16: { number_of_options: 2, to_inverse: false },
          mh17: { number_of_options: 2, to_inverse: false },
          mh18: { number_of_options: 2, to_inverse: false },
          mh19: { number_of_options: 2, to_inverse: false },
          mh110: { number_of_options: 2, to_inverse: false },
          mh111: { number_of_options: 2, to_inverse: false },
          mh112: { number_of_options: 2, to_inverse: false },
          mh113: { number_of_options: 2, to_inverse: false },
          mh114: { number_of_options: 2, to_inverse: false },
          mh115: { number_of_options: 2, to_inverse: false },
          mh116: { number_of_options: 2, to_inverse: false },
          mh117: { number_of_options: 2, to_inverse: false },
          mh118: { number_of_options: 2, to_inverse: false },
          mh119: { number_of_options: 2, to_inverse: false },
          mh120: { number_of_options: 2, to_inverse: false },
          mh121: { number_of_options: 2, to_inverse: false },
          mh122: { number_of_options: 2, to_inverse: false },
          mh123: { number_of_options: 2, to_inverse: false },
          mh124: { number_of_options: 2, to_inverse: false },
          mh125: { number_of_options: 2, to_inverse: false },
          mh126: { number_of_options: 2, to_inverse: false },
          mh127: { number_of_options: 2, to_inverse: false },
          mh128: { number_of_options: 2, to_inverse: false },
          mh129: { number_of_options: 2, to_inverse: false },
          mh130: { number_of_options: 2, to_inverse: false },
          mh131: { number_of_options: 2, to_inverse: false },
          mh132: { number_of_options: 2, to_inverse: false },
          mh133: { number_of_options: 2, to_inverse: false },
          mh134: { number_of_options: 2, to_inverse: false },
          mh135: { number_of_options: 2, to_inverse: false },
          mh136: { number_of_options: 2, to_inverse: false },
          mh137: { number_of_options: 2, to_inverse: false },
          mh138: { number_of_options: 2, to_inverse: false }
        };
      
      // Utiliser exactement la même logique que handleExportExcel
      const response = await apiService.getTimelineStats();
      if (!response.success) {
        throw new Error('Erreur lors du chargement des données');
      }
      
      const patients = response.stats;
      
      // Récupérer toutes les données comme dans handleExportExcel
      const [ohsResponse] = await Promise.all([
        apiService.getAllOHSResponses()
      ]);
      
      const ohsData = ohsResponse?.success ? ohsResponse.data : [];
      const sf36Data = [];
      const qor15Data = [];
      
      // Récupérer les données de mesures pour tous les patients
      let allMeasurementsData = {};
      try {
        for (const patient of patients) {
          try {
            const measurementResponse = await apiService.getPatientMeasurements(patient.patientId);
            if (measurementResponse.success && measurementResponse.data) {
              allMeasurementsData[patient.patientId] = measurementResponse.data;
            }
          } catch (error) {
            console.error(`Erreur récupération mesures pour ${patient.patientId}:`, error);
          }
        }
      } catch (error) {
        console.error('Erreur récupération mesures:', error);
      }
      
      // Récupérer toutes les réponses de questionnaires
      let allQuestionnaireResponses = [];
      try {
        const measurementResponses = await apiService.getAllQuestionnaireResponses();
        if (measurementResponses && measurementResponses.success && measurementResponses.data) {
          measurementResponses.data.forEach(response => {
            if (response.questionnaireType === 'WEIGHT_MEASUREMENT' || 
                response.questionnaireType === 'WAIST_MEASUREMENT' || 
                response.questionnaireType === 'KETONE_TEST' ||
                response.questionnaireType === 'BLOOD_PRESSURE_MEASUREMENT') {
              try {
                const responses = JSON.parse(response.responses);
                if (response.questionnaireType === 'BLOOD_PRESSURE_MEASUREMENT') {
                  allQuestionnaireResponses.push({
                    patientId: response.patientId,
                    taskId: response.taskId,
                    responses: response.responses,
                    value: responses,
                    type: response.questionnaireType,
                    version: response.version || 1,
                    completedAt: response.completedAt,
                    submissionDate: response.submissionDate,
                    language: response.language || 'de'
                  });
                } else {
                  Object.entries(responses).forEach(([key, value]) => {
                    allQuestionnaireResponses.push({
                      patientId: response.patientId,
                      taskId: response.taskId,
                      value: value,
                      type: response.questionnaireType,
                      version: response.version || 1,
                      completedAt: response.completedAt,
                      submissionDate: response.submissionDate,
                      language: response.language || 'de'
                    });
                  });
                }
              } catch (error) {
                console.error('Erreur parsing measurement response:', error);
              }
            }
          });
        }
      } catch (error) {
        console.error('Erreur récupération réponses mesures:', error);
      }
      
      // Générer les données Summary (même logique que handleExportExcel)
      const summaryData = [
        ['Patient ID', 'Gruppe', 'Overall Progress (%)', 'Expected Progress (%)', 'Total Tasks', 'Completed Tasks', 'Delay Level', 'Status']
      ];
      
      patients.forEach(patient => {
        summaryData.push([
          patient.patientId,
          patient.group || 'Fasting',
          patient.overallProgress || 0,
          patient.expectedProgress || 0,
          patient.totalTasks || 0,
          patient.completedTasks || 0,
          getDelayLevel(patient),
          patient.overallProgress >= patient.expectedProgress ? 'On Track' : 'Delayed'
        ]);
      });
      
      // Générer les données Detailed (même logique que handleExportExcel)
      const detailedData = [
        ['Patient ID', 'Phase', 'Day Number', 'Task Title', 'Task Type', 'Status', 'Completion Date', 'Value', 'Unit', 'Version', 'Language']
      ];
      
      // Récupérer les timelines détaillées pour tous les patients
      const detailedPatients = [];
      for (const patient of patients) {
        try {
          const timelineResponse = await apiService.getPatientTimeline(patient.patientId);
          if (timelineResponse.success) {
            detailedPatients.push({
              ...patient,
              timeline: timelineResponse.timeline
            });
          }
        } catch (error) {
          console.error(`Erreur récupération timeline pour ${patient.patientId}:`, error);
        }
      }
      
      // Traiter chaque patient comme dans handleExportExcel
      detailedPatients.forEach(patient => {
        if (patient.timeline) {
          patient.timeline.forEach(phase => {
            phase.days.forEach(day => {
              day.tasks.forEach(task => {
                // Fonction pour ajouter une ligne avec les données d'une version spécifique
                const addTaskVersion = (versionData, versionNumber) => {
                  let taskValue = '';
                  let taskUnit = '';
                  
                  if (versionData) {
                    if (versionData.type === 'KETONE_TEST') {
                      taskValue = versionData.value;
                      taskUnit = 'mg/dL';
                    } else if (versionData.type === 'WEIGHT_MEASUREMENT') {
                      taskValue = versionData.value;
                      taskUnit = 'kg';
                    } else if (versionData.type === 'WAIST_MEASUREMENT') {
                      taskValue = versionData.value;
                      taskUnit = 'cm';
                    } else if (versionData.type === 'BLOOD_PRESSURE_MEASUREMENT') {
                      try {
                        const responses = JSON.parse(versionData.responses || '{}');
                        const sbp = responses.sbp || '';
                        const dbp = responses.dbp || '';
                        const pulse = responses.pulse || '';
                        taskValue = `SBP: ${sbp}, DBP: ${dbp}, Pulse: ${pulse}`;
                        taskUnit = 'mmHg/bpm';
                      } catch (error) {
                        taskValue = versionData.value || '';
                        taskUnit = '';
                      }
                    } else if (versionData.type === 'OHS') {
                      const score = versionData.score || versionData.value;
                      taskValue = `Score: ${score}/48`;
                      taskUnit = 'points';
                    } else if (versionData.type === 'SF-36') {
                      taskValue = 'Completed';
                      taskUnit = '';
                    } else if (versionData.type === 'QoR-15') {
                      const score = versionData.score || versionData.value;
                      taskValue = `Score: ${score}/150`;
                      taskUnit = 'points';
                    } else {
                      taskValue = versionData.value;
                      taskUnit = '';
                    }
                  }

                  detailedData.push([
                    patient.patientId,
                    phase.title,
                    `Day ${day.dayNumber}`,
                    task.title,
                    task.taskType,
                    versionNumber === 1 ? 'Completed' : 'Modified',
                    versionData ? (() => {
                      const date = new Date(versionData.submissionDate || versionData.completedAt);
                      return date.toLocaleString('fr-FR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        timeZone: 'Europe/Paris'
                      });
                    })() : '',
                    taskValue,
                    taskUnit,
                    versionNumber,
                    versionData ? (versionData.language || 'en') : 'en'
                  ]);
                };

                // Si la tâche n'est pas complétée, ajouter une ligne Pending
                if (!task.completed) {
                  detailedData.push([
                    patient.patientId,
                    phase.title,
                    `Day ${day.dayNumber}`,
                    task.title,
                    task.taskType,
                    'Pending',
                    '',
                    '',
                    '',
                    0,
                    'en'
                  ]);
                } else {
                  // Récupérer toutes les versions de cette tâche (même logique que handleExportExcel)
                  let taskVersions = [];
                  
                  // Chercher dans les données OHS
                  if (ohsData && ohsData.length > 0) {
                    const ohsVersions = ohsData.filter(response => 
                      response.patientId === patient.patientId && response.taskId === task.taskId
                    );
                    taskVersions = taskVersions.concat(ohsVersions.map(v => ({ ...v, type: 'OHS' })));
                  }
                  
                  // Chercher dans les données SF-36
                  if (sf36Data && sf36Data.length > 0) {
                    const sf36Versions = sf36Data.filter(response => 
                      response.patientId === patient.patientId && response.taskId === task.taskId
                    );
                    taskVersions = taskVersions.concat(sf36Versions.map(v => ({ ...v, type: 'SF-36' })));
                  }
                  
                  // Chercher dans les données QoR-15
                  if (qor15Data && qor15Data.length > 0) {
                    const qor15Versions = qor15Data.filter(response => 
                      response.patientId === patient.patientId && response.taskId === task.taskId
                    );
                    taskVersions = taskVersions.concat(qor15Versions.map(v => ({ ...v, type: 'QoR-15' })));
                  }
                  
                  // Chercher dans les réponses de questionnaires pour les mesures (source unique pour éviter les doublons)
                  const measurementResponses = allQuestionnaireResponses.filter(response => 
                    response.patientId === patient.patientId && 
                    response.taskId === task.taskId &&
                    (response.type === 'WEIGHT_MEASUREMENT' || response.type === 'WAIST_MEASUREMENT' || response.type === 'KETONE_TEST' || response.type === 'BLOOD_PRESSURE_MEASUREMENT')
                  );
                  
                  if (measurementResponses.length > 0) {
                    taskVersions = taskVersions.concat(measurementResponses.map(v => ({ 
                      ...v, 
                      version: v.version || 1,
                      language: v.language || 'de',
                      submissionDate: v.submissionDate
                    })));
                  }
                  
                  // Trier les versions par date de completion
                  taskVersions.sort((a, b) => new Date(a.completedAt) - new Date(b.completedAt));
                  
                  // Ajouter une ligne pour chaque version
                  if (taskVersions.length > 0) {
                    taskVersions.forEach((version, index) => {
                      addTaskVersion(version, index + 1);
                    });
                  } else {
                    // Si pas de versions trouvées mais tâche marquée comme complétée
                    addTaskVersion(null, 1);
                  }
                }
              });
            });
          });
        }
      });
      
      // Ajouter toutes les réponses qui ne sont pas dans la timeline (même logique que handleExportExcel)
      const extractDayAndPhase = (taskId) => {
        const match = taskId.match(/day(\d+)-(.+)/);
        if (match) {
          const dayNumber = parseInt(match[1]);
          const taskType = match[2];
          
          let phase = 'Unknown';
          if (dayNumber >= 1 && dayNumber <= 5) {
            phase = 'Buchinger Fasten';
          } else if (dayNumber >= 6 && dayNumber <= 11) {
            phase = 'Intervalfasten';
          } else if (dayNumber >= 12 && dayNumber <= 23) {
            phase = 'Nach Operation';
          } else if (dayNumber >= 24) {
            phase = 'Follow-up';
          }
          
          return { dayNumber, phase, taskType };
        }
        return { dayNumber: 0, phase: 'Unknown', taskType: 'unknown' };
      };

      // Ajouter toutes les réponses OHS qui ne sont pas dans la timeline
      if (ohsData && ohsData.length > 0) {
        ohsData.forEach(response => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          
          const alreadyExported = detailedData.some(row => 
            row[0] === response.patientId && 
            row[2] === `Day ${dayNumber}` && 
            row[3].toLowerCase().includes('ohs')
          );
          
          if (!alreadyExported) {
            console.log(`Ajout OHS manquant: ${response.patientId} - ${response.taskId}`);
            detailedData.push([
              response.patientId,
              phase,
              `Day ${dayNumber}`,
              'OHS Questionnaire',
              'questionnaire',
              'Completed',
              response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }) : '',
              `Score: ${response.score}/48`,
              'points',
              response.version || 1,
              response.language || 'de'
            ]);
          }
        });
      }

      // Ajouter toutes les réponses SF-36 qui ne sont pas dans la timeline
      if (sf36Data && sf36Data.length > 0) {
        sf36Data.forEach(response => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          
          const alreadyExported = detailedData.some(row => 
            row[0] === response.patientId && 
            row[2] === `Day ${dayNumber}` && 
            row[3].toLowerCase().includes('sf-36')
          );
          
          if (!alreadyExported) {
            console.log(`Ajout SF-36 manquant: ${response.patientId} - ${response.taskId}`);
            detailedData.push([
              response.patientId,
              phase,
              `Day ${dayNumber}`,
              'SF-36 Questionnaire',
              'questionnaire',
              'Completed',
              response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }) : '',
              'Completed',
              '',
              response.version || 1,
              response.language || 'de'
            ]);
          }
        });
      }

      // Ajouter toutes les réponses QoR-15 qui ne sont pas dans la timeline
      if (qor15Data && qor15Data.length > 0) {
        qor15Data.forEach(response => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          
          const alreadyExported = detailedData.some(row => 
            row[0] === response.patientId && 
            row[2] === `Day ${dayNumber}` && 
            row[3].toLowerCase().includes('qor')
          );
          
          if (!alreadyExported) {
            console.log(`Ajout QoR-15 manquant: ${response.patientId} - ${response.taskId}`);
            detailedData.push([
              response.patientId,
              phase,
              `Day ${dayNumber}`,
              'QoR-15 Questionnaire',
              'questionnaire',
              'Completed',
              response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }) : '',
              `Score: ${response.score}/150`,
              'points',
              response.version || 1,
              response.language || 'de'
            ]);
          }
        });
      }
      
      // Générer les données OHS (même logique que handleExportExcel)
      const ohsDataFormatted = [
        ['Patient ID', 'Phase', 'Day Number', 'Date Completion', 'Version', 'Question ID', 'Question', 'Original Answer', 'Recoded Answer (0-4)', 'Answer Label', 'Language']
      ];
      
      // Traiter les données OHS comme dans handleExportExcel
      console.log('Traitement des données OHS pour preview:', ohsData.length);
      if (ohsData && ohsData.length > 0) {
        ohsData.forEach(response => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          console.log(`Traitement OHS: ${response.patientId} - ${response.taskId} - Day ${dayNumber} - Phase ${phase}`);
          
          try {
            const responses = JSON.parse(response.responses);
            const patientLanguage = response.language || 'de';
            
            Object.entries(responses).forEach(([questionId, answer]) => {
              const questionMapping = questionIdMapping[questionId];
              if (questionMapping) {
                const questionText = ohsQuestions[patientLanguage]?.[questionId] || questionId;
                const answerOptions = ohsOptions[patientLanguage]?.[questionId] || {};
                const answerLabel = answerOptions[answer] || answer;
                
                ohsDataFormatted.push([
                  response.patientId,
                  phase,
                  `Day ${dayNumber}`,
                  response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    timeZone: 'Europe/Paris'
                  }) : '',
                  response.version || 1,
                  questionMapping,
                  questionText,
                  answer,
                  answer,
                  answerLabel,
                  patientLanguage
                ]);
              }
            });
          } catch (error) {
            console.error('Erreur parsing OHS response:', error);
          }
        });
      }
      
      // Générer les données SF-36 (même logique que handleExportExcel)
      const sf36DataFormatted = [
        ['Patient ID', 'Phase', 'Day Number', 'Date Completion', 'Version', 'Question ID', 'Question', 'Original Answer', 'Answer Label', 'number_of_options', 'to_inverse', 'Language']
      ];
      
      // Traiter les données SF-36 comme dans handleExportExcel
      console.log('Traitement des données SF-36 pour preview:', sf36Data.length);
      if (sf36Data && sf36Data.length > 0) {
        sf36Data.forEach((response, index) => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          console.log(`Traitement SF-36: ${response.patientId} - ${response.taskId} - Day ${dayNumber} - Phase ${phase}`);
          
          try {
            const responses = JSON.parse(response.responses);
            const patientLanguage = response.language || 'de';
            
            // Utiliser la même logique que dans handleExportExcel
            const questions = sf36Questions[patientLanguage] || sf36Questions.de;
            const options = sf36Options[patientLanguage] || sf36Options.de;
            
            // Mapping simple des IDs de questions SF-36 vers Q1, Q2, Q3a, etc.
            const questionIdMapping = {
              // Q1-Q2: Questions générales sur la santé
              gh1: 'Q1',
              gh2: 'Q2',
              
              // Q3: Physical Functioning (10 sous-questions)
              pf1: 'Q3a', pf2: 'Q3b', pf3: 'Q3c', pf4: 'Q3d', pf5: 'Q3e',
              pf6: 'Q3f', pf7: 'Q3g', pf8: 'Q3h', pf9: 'Q3i', pf10: 'Q3j',
              
              // Q4: Role Physical (4 sous-questions)
              rp11: 'Q4a', rp12: 'Q4b', rp13: 'Q4c', rp14: 'Q4d',
              
              // Q5: Role Emotional (3 sous-questions)
              re11: 'Q5a', re12: 'Q5b', re13: 'Q5c',
              
              // Q6: Social Functioning
              sf1: 'Q6',
              
              // Q7: Bodily Pain
              bp1: 'Q7',
              
              // Q8: Bodily Pain (interférence)
              bp2: 'Q8',
              
              // Q9: Social Functioning (fréquence)
              sf2: 'Q9',
              
              // Q10: General Health (4 sous-questions)
              gh3: 'Q10a', gh4: 'Q10b', gh5: 'Q10c', gh6: 'Q10d',
              
              // Q11: General Health (évaluation actuelle)
              gh7: 'Q11',
              
              // Q12: Mental Health (38 sous-questions)
              mh11: 'Q12a', mh12: 'Q12b', mh13: 'Q12c', mh14: 'Q12d', mh15: 'Q12e',
              mh16: 'Q12f', mh17: 'Q12g', mh18: 'Q12h', mh19: 'Q12i', mh110: 'Q12j',
              mh111: 'Q12k', mh112: 'Q12l', mh113: 'Q12m', mh114: 'Q12n', mh115: 'Q12o',
              mh116: 'Q12p', mh117: 'Q12q', mh118: 'Q12r', mh119: 'Q12s', mh120: 'Q12t',
              mh121: 'Q12u', mh122: 'Q12v', mh123: 'Q12w', mh124: 'Q12x', mh125: 'Q12y',
              mh126: 'Q12z', mh127: 'Q12aa', mh128: 'Q12ab', mh129: 'Q12ac', mh130: 'Q12ad',
              mh131: 'Q12ae', mh132: 'Q12af', mh133: 'Q12ag', mh134: 'Q12ah', mh135: 'Q12ai',
              mh136: 'Q12aj', mh137: 'Q12ak', mh138: 'Q12al'
            };
            
            // Mapping pour déterminer si une question doit être inversée
            const inverseMapping = {
              // Physical Functioning (PF) - pas d'inversion nécessaire
              pf1: false, pf2: false, pf3: false, pf4: false, pf5: false,
              pf6: false, pf7: false, pf8: false, pf9: false, pf10: false,
              
              // Role Physical (RP) - pas d'inversion nécessaire
              rp11: false, rp12: false, rp13: false, rp14: false,
              
              // Bodily Pain (BP) - inversion nécessaire
              bp1: true,  // 1=None (meilleur) → 6=Very severe (pire)
              bp2: true,  // 1=Not at all (meilleur) → 5=Extremely (pire)
              
              // General Health (GH) - inversion nécessaire pour certaines questions
              gh1: true,  // 1=Excellent (meilleur) → 5=Poor (pire)
              gh2: true,  // 1=Much better (meilleur) → 5=Much worse (pire)
              gh3: true,  // 1=Definitely true (pire) → 5=Definitely false (meilleur)
              gh4: false, // 1=Definitely true (meilleur) → 5=Definitely false (pire)
              gh5: true,  // 1=Definitely true (pire) → 5=Definitely false (meilleur)
              gh6: false, // 1=Definitely true (meilleur) → 5=Definitely false (pire)
              gh7: true,  // 1=Excellent (meilleur) → 5=Poor (pire)
              
              // Social Functioning (SF) - inversion nécessaire
              sf1: true,  // 1=All of the time (pire) → 5=None of the time (meilleur)
              sf2: true,  // 1=Not at all (meilleur) → 5=Extremely (pire)
              
              // Role Emotional (RE) - pas d'inversion nécessaire
              re11: false, re12: false, re13: false,
              
              // Mental Health (MH) - inversion nécessaire pour certaines questions
              mh11: true, mh12: true, mh13: false, mh14: true, mh15: false,
              mh16: true, mh17: false, mh18: true, mh19: false, mh110: true,
              mh111: false, mh112: true, mh113: false, mh114: true, mh115: false,
              mh116: true, mh117: false, mh118: true, mh119: false, mh120: true,
              mh121: false, mh122: true, mh123: false, mh124: true, mh125: false,
              mh126: true, mh127: false, mh128: true, mh129: false, mh130: true,
              mh131: false, mh132: true, mh133: false, mh134: true, mh135: false,
              mh136: true, mh137: false, mh138: true
            };
            
            Object.entries(responses).forEach(([questionId, answerData]) => {
              // Gérer la nouvelle structure avec {value, label} ou l'ancienne structure
              let answer, answerLabel;
              if (typeof answerData === 'object' && answerData.value !== undefined) {
                // Nouvelle structure avec labels
                answer = answerData.value;
                answerLabel = answerData.label;
              } else {
                // Ancienne structure (fallback)
                answer = answerData;
                answerLabel = options[questionId] && options[questionId][answer] 
                  ? options[questionId][answer] 
                  : `Option ${answer}`;
              }
              
              const questionText = questions[questionId] || questionId;
              
              // S'assurer que answerLabel utilise les options en allemand
              if (options[questionId] && options[questionId][answer]) {
                answerLabel = options[questionId][answer];
              }
              
              // Calculer le nombre de réponses possibles pour cette question
              const numberOfOptions = options[questionId] ? Object.keys(options[questionId]).length : 0;
              
              // Déterminer si la question doit être inversée
              const toInverse = inverseMapping[questionId] || false;
              
              sf36DataFormatted.push([
                response.patientId,
                phase,
                `Day ${dayNumber}`,
                response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZone: 'Europe/Paris'
                }) : '',
                response.version || 1,
                questionIdMapping[questionId] || questionId,
                questionText,
                answer,
                answerLabel,
                numberOfOptions,
                toInverse,
                patientLanguage
              ]);
            });
          } catch (error) {
            console.error('Erreur parsing SF-36 response:', error);
          }
        });
      }

      // Traitement des données QoR-15 comme dans handleExportExcel
      console.log('Traitement des données QoR-15 pour preview:', qor15Data.length);
      const qor15DataFormatted = [
        ['Patient ID', 'Phase', 'Day', 'Completion Date', 'Version', 'Question ID', 'Question Text', 'Original Answer', 'Recoded Answer', 'Answer Label', 'Number of Options', 'To Inverse', 'Language']
      ];

      const qor15Questions = {
        en: {
          q1: 'Able to breathe easily',
          q2: 'Have had a good sleep',
          q3: 'Been able to enjoy food',
          q4: 'Feeling rested',
          q5: 'Able to look after personal toilet and hygiene unaided',
          q6: 'Able to communicate with family or friends',
          q7: 'Getting support from hospital doctors and nurses',
          q8: 'Able to return to work or usual home activities',
          q9: 'Moderate pain',
          q10: 'Severe pain',
          q11: 'Nausea',
          q12: 'Vomiting',
          q13: 'Feeling worried or anxious',
          q14: 'Feeling sad or depressed',
          q15: 'Feeling angry or irritable'
        },
        de: {
          q1: 'Konnten Sie frei und problemlos atmen?',
          q2: 'Haben Sie gut geschlafen?',
          q3: 'Konnten Sie das Essen genießen?',
          q4: 'Fühlten Sie sich ausgeruht?',
          q5: 'Konnten Sie sich eigenständig um Ihre Körperpflege/Toilette kümmern?',
          q6: 'Konnten Sie mit Familie oder Freunden kommunizieren?',
          q7: 'Haben Sie Unterstützung durch das Krankenhauspersonal erhalten?',
          q8: 'Waren Sie in der Lage, zu Ihrer Arbeit oder Ihren üblichen häuslichen Aktivitäten zurückzukehren?',
          q9: 'Hatten Sie mäßige Schmerzen?',
          q10: 'Hatten Sie starke Schmerzen?',
          q11: 'Hatten Sie Übelkeit?',
          q12: 'Mussten Sie sich übergeben (Erbrechen)?',
          q13: 'Fühlten Sie sich ängstlich oder besorgt?',
          q14: 'Fühlten Sie sich traurig oder niedergeschlagen?',
          q15: 'Fühlten Sie sich wütend oder gereizt?'
        }
      };

      const qor15Options = {
        en: {
          0: '0 - Never/Very poor',
          1: '1',
          2: '2',
          3: '3',
          4: '4',
          5: '5',
          6: '6',
          7: '7',
          8: '8',
          9: '9',
          10: '10 - Always/Very good'
        },
        de: {
          0: '0 - Nie/Sehr schlecht',
          1: '1',
          2: '2',
          3: '3',
          4: '4',
          5: '5',
          6: '6',
          7: '7',
          8: '8',
          9: '9',
          10: '10 - Immer/Sehr gut'
        }
      };

      if (qor15Data && qor15Data.length > 0) {
        qor15Data.forEach(response => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          console.log(`Traitement QoR-15: ${response.patientId} - ${response.taskId} - Day ${dayNumber} - Phase ${phase}`);
          
          try {
            const responses = JSON.parse(response.responses);
            const patientLanguage = response.language || 'de';
            const questions = qor15Questions[patientLanguage] || qor15Questions.en;
            const options = qor15Options[patientLanguage] || qor15Options.en;
            
            Object.entries(responses).forEach(([questionId, answerData]) => {
              // Gérer la nouvelle structure avec {value, label} ou l'ancienne structure
              let answer, answerLabel;
              if (typeof answerData === 'object' && answerData.value !== undefined) {
                // Nouvelle structure avec labels
                answer = answerData.value;
                answerLabel = answerData.label;
              } else {
                // Ancienne structure (fallback)
                answer = answerData;
                answerLabel = options[answer] || `Value ${answer}`;
              }
              
              const questionText = questions[questionId] || questionId;
              
              // QoR-15: Questions 11-15 sont inversées (reverse scored)
              const isReverseScored = ['q11', 'q12', 'q13', 'q14', 'q15'].includes(questionId);
              const recodedAnswer = isReverseScored ? (10 - answer) : answer;
              
              qor15DataFormatted.push([
                response.patientId,
                phase,
                `Day ${dayNumber}`,
                response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZone: 'Europe/Paris'
                }) : '',
                response.version || 1,
                questionId,
                questionText,
                answer,
                recodedAnswer,
                answerLabel,
                11, // QoR-15 a toujours 11 options (0-10)
                isReverseScored,
                patientLanguage
              ]);
            });
          } catch (error) {
            console.error('Erreur parsing QoR-15 response:', error);
          }
        });
      }
      
      // Construire les onglets Preview pour DEGS / Infection / PROMIS-33 (même logique que l'export)
      let degsRows = [['Patient ID','Task ID','Completion Date','Version','Question ID','Answer','Language']];
      let infectionRows = [['Patient ID','Task ID','Completion Date','Version','Question ID','Question','Answer','Answer Label','Language']];
      let promisRows = [['Patient ID','Task ID','Completion Date','Version','Question ID','Question','Answer','Answer Label','Language']];
      let hoosRows = [['Patient ID','Task ID','Completion Date','Version','Question ID','Question','Answer','Answer Label','Language']];
      try {
        const allQRResp = await apiService.getAllQuestionnaireResponses();
        const allQR = allQRResp && allQRResp.success && allQRResp.data ? allQRResp.data : [];

        const degsList = allQR.filter(r => String(r.questionnaireType).toLowerCase() === 'degs');
        const infectionList = allQR.filter(r => String(r.questionnaireType).toUpperCase() === 'INFECTION');
        const promisList = allQR.filter(r => String(r.questionnaireType).toUpperCase() === 'PROMIS-33');
        const hoosList = allQR.filter(r => String(r.questionnaireType).toUpperCase() === 'HOOS');

        const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', timeZone:'Europe/Paris' }) : '';

        degsList.forEach(r => {
          try {
            const respObj = JSON.parse(r.responses || '{}');
            let hasQ0a = false;
            let hasQ0b = false;
            Object.entries(respObj).forEach(([qid, ans]) => {
              if (/^Q\d+(a|b)?$/i.test(String(qid))) {
                if (/^Q0a$/i.test(String(qid))) hasQ0a = true;
                if (/^Q0b$/i.test(String(qid))) hasQ0b = true;
                degsRows.push([
                  r.patientId,
                  r.taskId,
                  fmtDate(r.completedAt),
                  r.version || 1,
                  String(qid).replace(/^Q/i, ''),
                  typeof ans === 'object' ? JSON.stringify(ans) : ans,
                  r.language || 'de'
                ]);
              }
            });
            // Fallback pour anciennes entrées: générer Q0a/Q0b si absents
            if (!hasQ0a && (respObj.birthMonth || respObj.birthYear)) {
              const mm = String(respObj.birthMonth || '').padStart(2, '0');
              const yy = respObj.birthYear || '';
              const val = mm && yy ? `${mm}/${yy}` : (yy || mm);
              if (val) {
                degsRows.push([
                  r.patientId,
                  r.taskId,
                  fmtDate(r.completedAt),
                  r.version || 1,
                  '0a',
                  val,
                  r.language || 'de'
                ]);
              }
            }
            if (!hasQ0b && respObj.gender) {
              degsRows.push([
                r.patientId,
                r.taskId,
                fmtDate(r.completedAt),
                r.version || 1,
                '0b',
                respObj.gender,
                r.language || 'de'
              ]);
            }
          } catch {}
        });

        infectionList.forEach(r => {
          try {
            const parsed = (() => { try { return typeof r.responses === 'string' ? JSON.parse(r.responses || '{}') : (r.responses || {}); } catch { return {}; } })();
            const base = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? (parsed.responses || parsed.answers || parsed)
              : parsed;
            const entries = Array.isArray(base)
              ? base.map((val, idx) => [String(idx + 1), val])
              : Object.entries(base || {});
            entries.forEach(([qid, ans]) => {
              const val = (ans && typeof ans === 'object' && 'value' in ans) ? ans.value : (typeof ans === 'object' ? JSON.stringify(ans) : ans);
              const questionText = INFECTION_QUESTIONS[qid] || qid;
              const answerLabel = INFECTION_ANSWERS[String(val).toLowerCase()] || val;
              infectionRows.push([
                r.patientId,
                r.taskId,
                fmtDate(r.completedAt),
                r.version || 1,
                qid,
                questionText,
                val,
                answerLabel,
                r.language || 'de'
              ]);
            });
          } catch {}
        });

        promisList.forEach(r => {
          try {
            const parsed = (() => { try { return typeof r.responses === 'string' ? JSON.parse(r.responses || '{}') : (r.responses || {}); } catch { return {}; } })();
            const base = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? (parsed.responses || parsed.answers || parsed)
              : parsed;
            const entries = Array.isArray(base)
              ? base.map((val, idx) => [String(idx + 1), val])
              : Object.entries(base || {});
            entries.forEach(([qid, ans]) => {
              const val = (ans && typeof ans === 'object' && 'value' in ans) ? ans.value : (typeof ans === 'object' ? JSON.stringify(ans) : ans);
              const questionText = PROMIS_QUESTIONS[qid] || qid;
              const answerLabel = PROMIS_ANSWERS[qid] && PROMIS_ANSWERS[qid][val] ? PROMIS_ANSWERS[qid][val] : val;
              promisRows.push([
                r.patientId,
                r.taskId,
                fmtDate(r.completedAt),
                r.version || 1,
                qid,
                questionText,
                val,
                answerLabel,
                r.language || 'de'
              ]);
            });
          } catch {}
        });

        hoosList.forEach(r => {
          try {
            const parsed = (() => { try { return typeof r.responses === 'string' ? JSON.parse(r.responses || '{}') : (r.responses || {}); } catch { return {}; } })();
            const base = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? (parsed.responses || parsed.answers || parsed)
              : parsed;
            const entries = Object.entries(base || {});
            entries.forEach(([qid, ans]) => {
              const val = (ans && typeof ans === 'object' && 'value' in ans) ? ans.value : (typeof ans === 'object' ? JSON.stringify(ans) : ans);
              const questionText = HOOS_QUESTIONS[qid] || qid;
              const answerLabel = (HOOS_ANSWERS[qid] && HOOS_ANSWERS[qid][val] !== undefined) ? HOOS_ANSWERS[qid][val] : val;
              hoosRows.push([
                r.patientId,
                r.taskId,
                fmtDate(r.completedAt),
                r.version || 1,
                qid,
                questionText,
                val,
                answerLabel,
                r.language || 'de'
              ]);
            });
          } catch {}
        });
      } catch (e) {
        console.error('Erreur récupération réponses pour preview:', e);
      }
      
      setExcelPreviewData({
        summary: summaryData,
        detailed: detailedData,
        degs: degsRows,
        infection: infectionRows,
        promis: promisRows,
        hoos: hoosRows
      });
      
      setShowExcelPreview(true);
    } catch (error) {
      console.error('Erreur génération preview:', error);
      setError('Erreur lors de la génération du preview');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      
      // Récupérer les données détaillées pour chaque patient avec délai
      const detailedData = [];
      
      for (let i = 0; i < patients.length; i++) {
        const patient = patients[i];
        try {
          // Ajouter un délai entre les requêtes pour éviter le rate limiting
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
          const response = await apiService.getPatientHistory(patient.patientId);
          if (response.success) {
            detailedData.push({
              patientId: patient.patientId,
              startDate: patient.startDate,
              language: patient.language || 'en',
              group: patient.group || 'Fasting',
              overallProgress: `${response.statistics.overallProgress}%`,
              completedTasks: response.statistics.completedTasks,
              totalTasks: response.statistics.totalTasks,
              completedDays: response.statistics.completedDays,
              totalDays: response.statistics.totalDays,
              timeline: response.timeline
            });
          }
        } catch (error) {
          console.error(`Erreur récupération données pour ${patient.patientId}:`, error);
          // Ajouter les données de base même en cas d'erreur
          detailedData.push({
      patientId: patient.patientId,
      startDate: patient.startDate,
      language: patient.language || 'en',
      group: patient.group || 'Fasting',
            overallProgress: `${patient.progressPercentage || 0}%`,
            completedTasks: 'N/A',
            totalTasks: 'N/A',
            completedDays: 'N/A',
            totalDays: 'N/A',
            timeline: null
          });
        }
      }

      // Récupérer les données OHS
      let ohsData = [];
      try {
        console.log('=== DÉBUT RÉCUPÉRATION OHS ===');
        console.log('Récupération des données OHS...');
        const ohsResponse = await apiService.getAllOHSResponses();
        console.log('Réponse OHS reçue:', ohsResponse);
        console.log('Type de réponse:', typeof ohsResponse);
        console.log('Success:', ohsResponse?.success);
        console.log('Data:', ohsResponse?.data);
        if (ohsResponse && ohsResponse.success) {
          ohsData = ohsResponse.data;
          console.log('Données OHS extraites:', ohsData);
          console.log('Type de données:', typeof ohsData);
          console.log('Longueur:', Array.isArray(ohsData) ? ohsData.length : 'Pas un tableau');
          if (Array.isArray(ohsData)) {
            ohsData.forEach((item, index) => {
              console.log(`OHS ${index}:`, {
                patientId: item.patientId,
                taskId: item.taskId,
                version: item.version,
                completedAt: item.completedAt
              });
            });
          }
        } else {
          console.log('Réponse OHS échouée ou pas de données');
        }
        console.log('=== FIN RÉCUPÉRATION OHS ===');
      } catch (error) {
        console.error('Erreur récupération OHS:', error);
        console.error('Stack trace:', error.stack);
      }

      // Récupérer les données SF-36 (désactivé)
      const sf36Data = [];

      // Récupérer les données QoR-15 (désactivé)
      const qor15Data = [];

      // Récupérer les données de mesures pour tous les patients
      let allMeasurementsData = {};
      try {
        console.log('=== DÉBUT RÉCUPÉRATION MESURES ===');
        for (const patient of patients) {
          try {
            const measurementResponse = await apiService.getPatientMeasurements(patient.patientId);
            if (measurementResponse.success && measurementResponse.data) {
              allMeasurementsData[patient.patientId] = measurementResponse.data;
            }
          } catch (error) {
            console.error(`Erreur récupération mesures pour ${patient.patientId}:`, error);
          }
        }
        console.log('=== FIN RÉCUPÉRATION MESURES ===');
      } catch (error) {
        console.error('Erreur récupération mesures:', error);
      }

      // Créer le contenu CSV détaillé
      const headers = [
        'Patient ID', 
        'Start Date', 
        'Language', 
        'Gruppe', 
        'Overall Progress', 
        'Completed Tasks', 
        'Total Tasks',
        'Completed Days',
        'Total Days'
      ];
      
    const csvContent = [
      headers.join(','),
        ...detailedData.map(row => [
          row.patientId,
          row.startDate,
          row.language,
          row.group,
          row.overallProgress,
          row.completedTasks,
          row.totalTasks,
          row.completedDays,
          row.totalDays
        ].join(','))
      ].join('\n');

      // Préparer les données pour Excel

      // Feuille 1: Résumé
      const summaryData = [
        headers,
        ...detailedData.map(row => [
          row.patientId,
          row.startDate,
          row.language,
          row.group,
          row.overallProgress,
          row.completedTasks,
          row.totalTasks,
          row.completedDays,
          row.totalDays
        ])
      ];

      // Feuille 2: Tâches détaillées
      const detailedTasksData = [
        ['Patient ID', 'Phase', 'Day', 'Task Title', 'Task Type', 'Status', 'Submission Date', 'Value', 'Unit', 'Version', 'Language']
      ];

      // Récupérer toutes les réponses de questionnaires pour obtenir les valeurs
      const allQuestionnaireResponses = [];
      
      // Fonction pour déterminer le statut d'une tâche (Pending, Completed, Modified)
      const getTaskStatus = (task, patientId) => {
        if (!task.completed) {
          return 'Pending';
        }
        
        // Vérifier si la tâche a été modifiée en cherchant dans l'historique
        // Chercher dans les données OHS
        if (ohsData && ohsData.length > 0) {
          const ohsResponses = ohsData.filter(response => 
            response.patientId === patientId && response.taskId === task.taskId
          );
          if (ohsResponses.length > 1) {
            return 'Modified';
          }
        }
        
        // Chercher dans les données SF-36
        if (sf36Data && sf36Data.length > 0) {
          const sf36Responses = sf36Data.filter(response => 
            response.patientId === patientId && response.taskId === task.taskId
          );
          if (sf36Responses.length > 1) {
            return 'Modified';
          }
        }
        
        // Chercher dans les données QoR-15
        if (qor15Data && qor15Data.length > 0) {
          const qor15Responses = qor15Data.filter(response => 
            response.patientId === patientId && response.taskId === task.taskId
          );
          if (qor15Responses.length > 1) {
            return 'Modified';
          }
        }
        
        // Chercher dans les mesures (poids, tour de taille, cétoniques)
        const patientMeasurements = allMeasurementsData[patientId];
        if (patientMeasurements) {
          const { weight, waist, ketones } = patientMeasurements;
          
          // Vérifier les mesures de poids
          if (weight && weight.length > 0) {
            const weightResponses = weight.filter(m => m.taskId === task.taskId);
            if (weightResponses.length > 1) {
              return 'Modified';
            }
          }
          
          // Vérifier les mesures de tour de taille
          if (waist && waist.length > 0) {
            const waistResponses = waist.filter(m => m.taskId === task.taskId);
            if (waistResponses.length > 1) {
              return 'Modified';
            }
          }
          
          // Vérifier les tests de cétoniques
          if (ketones && ketones.length > 0) {
            const ketoneResponses = ketones.filter(m => m.taskId === task.taskId);
            if (ketoneResponses.length > 1) {
              return 'Modified';
            }
          }
        }
        
        return 'Completed';
      };
      
      // Ajouter les scores finaux OHS (pas les réponses individuelles)
      if (ohsData && ohsData.length > 0) {
        ohsData.forEach(response => {
          allQuestionnaireResponses.push({
            patientId: response.patientId,
            taskId: response.taskId,
            value: response.score, // Score final OHS (0-48)
            type: 'OHS'
          });
        });
      }

      // Ajouter les scores finaux SF-36 (pas les réponses individuelles)
      if (sf36Data && sf36Data.length > 0) {
        sf36Data.forEach(response => {
          allQuestionnaireResponses.push({
            patientId: response.patientId,
            taskId: response.taskId,
            value: response.score, // Scores par domaine SF-36
            type: 'SF-36',
            version: response.version || 1, // Ajouter la version
            completedAt: response.completedAt
          });
        });
      }

      // Ajouter les scores finaux QoR-15 (pas les réponses individuelles)
      if (qor15Data && qor15Data.length > 0) {
        qor15Data.forEach(response => {
          allQuestionnaireResponses.push({
            patientId: response.patientId,
            taskId: response.taskId,
            value: response.score, // Score final QoR-15 (0-150)
            type: 'QoR-15'
          });
        });
      }

      // Récupérer les réponses pour les mesures (poids, tour de taille, cétoniques, tension artérielle)
      try {
        const measurementResponses = await apiService.getAllQuestionnaireResponses();
        if (measurementResponses && measurementResponses.success && measurementResponses.data) {
          measurementResponses.data.forEach(response => {
            if (response.questionnaireType === 'WEIGHT_MEASUREMENT' || 
                response.questionnaireType === 'WAIST_MEASUREMENT' || 
                response.questionnaireType === 'KETONE_TEST' ||
                response.questionnaireType === 'BLOOD_PRESSURE_MEASUREMENT') {
              try {
                const responses = JSON.parse(response.responses);
                if (response.questionnaireType === 'BLOOD_PRESSURE_MEASUREMENT') {
                  // Pour la tension artérielle, ajouter une seule entrée avec toutes les valeurs
                  allQuestionnaireResponses.push({
                    patientId: response.patientId,
                    taskId: response.taskId,
                    responses: response.responses, // Garder les réponses complètes
                    value: responses, // Stocker toutes les valeurs
                    type: response.questionnaireType,
                    version: response.version || 1,
                    completedAt: response.completedAt,
                    submissionDate: response.submissionDate,
                    language: response.language || 'de'
                  });
                } else {
                  // Pour les autres mesures, traiter chaque valeur séparément
                  Object.entries(responses).forEach(([key, value]) => {
                    allQuestionnaireResponses.push({
                      patientId: response.patientId,
                      taskId: response.taskId,
                      value: value,
                      type: response.questionnaireType,
                      version: response.version || 1,
                      completedAt: response.completedAt,
                      submissionDate: response.submissionDate,
                      language: response.language || 'de'
                    });
                  });
                }
              } catch (error) {
                console.error('Erreur parsing measurement response:', error);
              }
            }
          });
        }
      } catch (error) {
        console.error('Erreur récupération réponses mesures:', error);
      }

      detailedData.forEach(patient => {
        if (patient.timeline) {
          patient.timeline.forEach(phase => {
            phase.days.forEach(day => {
              day.tasks.forEach(task => {
                // Fonction pour ajouter une ligne avec les données d'une version spécifique
                const addTaskVersion = (versionData, versionNumber) => {
                  let taskValue = '';
                  let taskUnit = '';
                  
                  if (versionData) {
                    if (versionData.type === 'KETONE_TEST') {
                      taskValue = versionData.value;
                      taskUnit = 'mg/dL';
                    } else if (versionData.type === 'WEIGHT_MEASUREMENT') {
                      taskValue = versionData.value;
                      taskUnit = 'kg';
                    } else if (versionData.type === 'WAIST_MEASUREMENT') {
                      taskValue = versionData.value;
                      taskUnit = 'cm';
                    } else if (versionData.type === 'BLOOD_PRESSURE_MEASUREMENT') {
                      // Décomposer les mesures de tension artérielle
                      try {
                        const responses = JSON.parse(versionData.responses || '{}');
                        const sbp = responses.sbp || '';
                        const dbp = responses.dbp || '';
                        const pulse = responses.pulse || '';
                        taskValue = `SBP: ${sbp}, DBP: ${dbp}, Pulse: ${pulse}`;
                        taskUnit = 'mmHg/bpm';
                      } catch (error) {
                        taskValue = versionData.value || '';
                        taskUnit = '';
                      }
                    } else if (versionData.type === 'OHS') {
                      // Pour OHS, utiliser le score depuis les données d'historique
                      const score = versionData.score || versionData.value;
                      taskValue = `Score: ${score}/48`;
                      taskUnit = 'points';
                    } else if (versionData.type === 'SF-36') {
                      // Pour SF-36, ne pas afficher de score
                      taskValue = 'Completed';
                      taskUnit = '';
                    } else if (versionData.type === 'QoR-15') {
                      // Pour QoR-15, utiliser le score depuis les données d'historique
                      const score = versionData.score || versionData.value;
                      taskValue = `Score: ${score}/150`;
                      taskUnit = 'points';
                    } else {
                      taskValue = versionData.value;
                      taskUnit = '';
                    }
                  }

                  detailedTasksData.push([
                    patient.patientId,
                    phase.title,
                    `Day ${day.dayNumber}`,
                    task.title,
                    task.taskType,
                    versionNumber === 1 ? 'Completed' : 'Modified',
                    versionData ? (() => {
                      const date = new Date(versionData.submissionDate || versionData.completedAt);
                      return date.toLocaleString('fr-FR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        timeZone: 'Europe/Paris'
                      });
                    })() : '',
                    taskValue,
                    taskUnit,
                    versionNumber, // Ajouter le numéro de version
                    versionData ? (versionData.language || 'en') : 'en' // Langue utilisée lors de la completion
                  ]);
                };

                // Si la tâche n'est pas complétée, ajouter une ligne Pending
                if (!task.completed) {
                  detailedTasksData.push([
                    patient.patientId,
                    phase.title,
                    `Day ${day.dayNumber}`,
                    task.title,
                    task.taskType,
                    'Pending',
                    '',
                    '',
                    '',
                    0,
                    'en' // Langue par défaut pour les tâches en attente
                  ]);
                } else {
                  // Récupérer toutes les versions de cette tâche
                  let taskVersions = [];
                  
                  // Chercher dans les données OHS
                  if (ohsData && ohsData.length > 0) {
                    const ohsVersions = ohsData.filter(response => 
                      response.patientId === patient.patientId && response.taskId === task.taskId
                    );
                    console.log(`Versions OHS trouvées pour ${patient.patientId} - ${task.taskId}:`, ohsVersions);
                    console.log(`Nombre de versions OHS: ${ohsVersions.length}`);
                    ohsVersions.forEach((version, index) => {
                      console.log(`  Version ${index + 1}: ${version.version} - ${version.completedAt}`);
                    });
                    taskVersions = taskVersions.concat(ohsVersions.map(v => ({ ...v, type: 'OHS' })));
                  }
                  
                  // Chercher dans les données SF-36
                  if (sf36Data && sf36Data.length > 0) {
                    const sf36Versions = sf36Data.filter(response => 
                      response.patientId === patient.patientId && response.taskId === task.taskId
                    );
                    console.log(`Versions SF-36 trouvées pour ${patient.patientId} - ${task.taskId}:`, sf36Versions);
                    console.log(`Nombre de versions SF-36: ${sf36Versions.length}`);
                    sf36Versions.forEach((version, index) => {
                      console.log(`  Version ${index + 1}: ${version.version} - ${version.completedAt}`);
                    });
                    taskVersions = taskVersions.concat(sf36Versions.map(v => ({ ...v, type: 'SF-36' })));
                  }
                  
                  // Chercher dans les données QoR-15
                  if (qor15Data && qor15Data.length > 0) {
                    const qor15Versions = qor15Data.filter(response => 
                      response.patientId === patient.patientId && response.taskId === task.taskId
                    );
                    console.log(`Versions QoR-15 trouvées pour ${patient.patientId} - ${task.taskId}:`, qor15Versions);
                    taskVersions = taskVersions.concat(qor15Versions.map(v => ({ ...v, type: 'QoR-15' })));
                  }
                  
                  // Chercher dans les réponses de questionnaires pour les mesures (source unique pour éviter les doublons)
                  const measurementResponses = allQuestionnaireResponses.filter(response => 
                    response.patientId === patient.patientId && 
                    response.taskId === task.taskId &&
                    (response.type === 'WEIGHT_MEASUREMENT' || response.type === 'WAIST_MEASUREMENT' || response.type === 'KETONE_TEST' || response.type === 'BLOOD_PRESSURE_MEASUREMENT')
                  );
                  
                  if (measurementResponses.length > 0) {
                    taskVersions = taskVersions.concat(measurementResponses.map(v => ({ 
                      ...v, 
                      version: v.version || 1,
                      language: v.language || 'de',
                      submissionDate: v.submissionDate
                    })));
                  }
                  
                  // Trier les versions par date de completion
                  taskVersions.sort((a, b) => new Date(a.completedAt) - new Date(b.completedAt));
                  
                  console.log(`Toutes les versions trouvées pour ${patient.patientId} - ${task.taskId}:`, taskVersions);
                  
                  // Ajouter une ligne pour chaque version
                  if (taskVersions.length > 0) {
                    taskVersions.forEach((version, index) => {
                      console.log(`Ajout version ${index + 1}:`, version);
                      addTaskVersion(version, index + 1);
                    });
                  } else {
                    // Si pas de versions trouvées mais tâche marquée comme complétée
                    console.log(`Aucune version trouvée pour ${patient.patientId} - ${task.taskId}, ajout version par défaut`);
                    addTaskVersion(null, 1);
                  }
                }
              });
            });
          });
        }
      });

      // EXPORTER TOUTES LES RÉPONSES DE QUESTIONNAIRES (même celles qui ne sont pas dans la timeline)
      console.log('=== EXPORT DE TOUTES LES RÉPONSES DE QUESTIONNAIRES ===');
      
      // Fonction pour extraire le jour et la phase depuis le taskId
      const extractDayAndPhase = (taskId) => {
        const match = taskId.match(/day(\d+)-(.+)/);
        if (match) {
          const dayNumber = parseInt(match[1]);
          const taskType = match[2];
          
          // Déterminer la phase basée sur le jour
          let phase = 'Unknown';
          if (dayNumber >= 1 && dayNumber <= 5) {
            phase = 'Buchinger Fasten';
          } else if (dayNumber >= 6 && dayNumber <= 11) {
            phase = 'Intervalfasten';
          } else if (dayNumber >= 12 && dayNumber <= 23) {
            phase = 'Nach Operation';
          } else if (dayNumber >= 24) {
            phase = 'Follow-up';
          }
          
          return { dayNumber, phase, taskType };
        }
        return { dayNumber: 0, phase: 'Unknown', taskType: 'unknown' };
      };

      // Ajouter toutes les réponses OHS qui ne sont pas dans la timeline
      if (ohsData && ohsData.length > 0) {
        ohsData.forEach(response => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          
          // Vérifier si cette réponse n'est pas déjà dans detailedTasksData
          const alreadyExported = detailedTasksData.some(row => 
            row[0] === response.patientId && 
            row[2] === `Day ${dayNumber}` && 
            row[3].toLowerCase().includes('ohs')
          );
          
          if (!alreadyExported) {
            console.log(`Ajout OHS manquant: ${response.patientId} - ${response.taskId}`);
            detailedTasksData.push([
              response.patientId,
              phase,
              `Day ${dayNumber}`,
              'OHS Questionnaire',
              'questionnaire',
              'Completed',
              response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }) : '',
              `Score: ${response.score}/48`,
              'points',
              response.version || 1,
              response.language || 'de'
            ]);
          }
        });
      }

      // Ajouter toutes les réponses SF-36 qui ne sont pas dans la timeline
      if (sf36Data && sf36Data.length > 0) {
        sf36Data.forEach(response => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          
          // Vérifier si cette réponse n'est pas déjà dans detailedTasksData
          const alreadyExported = detailedTasksData.some(row => 
            row[0] === response.patientId && 
            row[2] === `Day ${dayNumber}` && 
            row[3].toLowerCase().includes('sf-36')
          );
          
          if (!alreadyExported) {
            console.log(`Ajout SF-36 manquant: ${response.patientId} - ${response.taskId}`);
            detailedTasksData.push([
              response.patientId,
              phase,
              `Day ${dayNumber}`,
              'SF-36 Questionnaire',
              'questionnaire',
              'Completed',
              response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }) : '',
              'Completed',
              '',
              response.version || 1,
              response.language || 'de'
            ]);
          }
        });
      }

      // Ajouter toutes les réponses QoR-15 qui ne sont pas dans la timeline
      if (qor15Data && qor15Data.length > 0) {
        qor15Data.forEach(response => {
          const { dayNumber, phase } = extractDayAndPhase(response.taskId);
          
          // Vérifier si cette réponse n'est pas déjà dans detailedTasksData
          const alreadyExported = detailedTasksData.some(row => 
            row[0] === response.patientId && 
            row[2] === `Day ${dayNumber}` && 
            row[3].toLowerCase().includes('qor')
          );
          
          if (!alreadyExported) {
            console.log(`Ajout QoR-15 manquant: ${response.patientId} - ${response.taskId}`);
            detailedTasksData.push([
              response.patientId,
              phase,
              `Day ${dayNumber}`,
              'QoR-15 Questionnaire',
              'questionnaire',
              'Completed',
              response.completedAt ? new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }) : '',
              `Score: ${response.score}/150`,
              'points',
              response.version || 1,
              response.language || 'de'
            ]);
          }
        });
      }

      console.log('=== FIN EXPORT DE TOUTES LES RÉPONSES ===');

      // Créer le fichier CSV avec les deux feuilles
      const summaryCSV = [
        '=== SUMMARY ===',
        '',
        ...summaryData.map(row => row.join(','))
    ].join('\n');

      // Mapping des IDs de questions vers les numéros de question OHS (version officielle)
      const questionIdMapping = {
        body_side: 'Q0',
        pain: 'Q1',
        night_pain: 'Q2',
        sudden_pain: 'Q3',
        limping: 'Q4',
        walking_duration: 'Q5',
        stairs: 'Q6',
        socks: 'Q7',
        standing_up: 'Q8',
        transport: 'Q9',
        washing: 'Q10',
        shopping: 'Q11',
        work_interference: 'Q12'
      };

      // Feuille 3: Données OHS
      const ohsDataFormatted = [
        ['Patient ID', 'Phase', 'Day Number', 'Date Completion', 'Version', 'Question ID', 'Question', 'Original Answer', 'Recoded Answer (0-4)', 'Answer Label', 'Language']
      ];

      // Feuille 4: Données SF-36
      const sf36DataFormatted = [
        ['Patient ID', 'Phase', 'Day Number', 'Date Completion', 'Version', 'Question ID', 'Question', 'Original Answer', 'Answer Label', 'number_of_options', 'to_inverse', 'Language']
      ];

      // Questions OHS avec leurs labels (version officielle validée)
      const ohsQuestions = {
        en: {
          body_side: 'On which side of your body is the affected joint for which you are receiving treatment?',
          pain: 'How would you describe the pain you usually have in your hip?',
          night_pain: 'Have you been troubled by pain from your hip in bed at night?',
          sudden_pain: 'Have you had any sudden, severe pain (shooting, stabbing, or spasms) from your affected hip?',
          limping: 'Have you been limping when walking because of your hip?',
          walking_duration: 'For how long have you been able to walk before the pain in your hip becomes severe (with or without a walking aid)?',
          stairs: 'Have you been able to climb a flight of stairs?',
          socks: 'Have you been able to put on a pair of socks, stockings or tights?',
          standing_up: 'After a meal (sat at a table), how painful has it been for you to stand up from a chair because of your hip?',
          transport: 'Have you had any trouble getting in and out of a car or using public transportation because of your hip?',
          washing: 'Have you had any trouble with washing and drying yourself (all over) because of your hip?',
          shopping: 'Could you do the household shopping on your own?',
          work_interference: 'How much has pain from your hip interfered with your usual work, including housework?'
        },
        de: {
          body_side: 'Auf welcher Körperseite befindet sich das betroffene Gelenk, für das Sie eine Behandlung erhalten?',
          pain: 'Wie würden Sie den Schmerz beschreiben, den Sie normalerweise von Ihrer Hüfte haben?',
          night_pain: 'Wurden Sie nachts im Bett durch Schmerzen von Ihrer Hüfte gestört?',
          sudden_pain: 'Hatten Sie plötzliche, starke Schmerzen (stechende, stichartige oder krampfartige) von Ihrer betroffenen Hüfte?',
          limping: 'Hinken Sie beim Gehen wegen Ihrer Hüfte?',
          walking_duration: 'Wie lange konnten Sie laufen, bevor der Schmerz von Ihrer Hüfte stark wird (mit oder ohne Gehhilfe)?',
          stairs: 'Konnten Sie eine Treppe hinaufsteigen?',
          socks: 'Konnten Sie ein Paar Socken, Strümpfe oder Strumpfhosen anziehen?',
          standing_up: 'Nach einer Mahlzeit (am Tisch gesessen), wie schmerzhaft war es für Sie, von einem Stuhl aufzustehen wegen Ihrer Hüfte?',
          transport: 'Hatten Sie Probleme beim Ein- und Aussteigen aus einem Auto oder bei der Nutzung öffentlicher Verkehrsmittel wegen Ihrer Hüfte?',
          washing: 'Hatten Sie Probleme beim Waschen und Trocknen (am ganzen Körper) wegen Ihrer Hüfte?',
          shopping: 'Konnten Sie alleine einkaufen gehen?',
          work_interference: 'Wie sehr haben Schmerzen von Ihrer Hüfte Ihre übliche Arbeit, einschließlich Hausarbeit, beeinträchtigt?'
        }
      };

      // Options de réponses OHS (version officielle)
      const ohsOptions = {
        en: {
          body_side: {
            1: 'Right', 2: 'Left', 3: 'Both'
          },
          pain: {
            1: 'None', 2: 'Very mild', 3: 'Mild', 4: 'Moderate', 5: 'Severe'
          },
          night_pain: {
            1: 'No nights', 2: 'Only 1 or 2 nights', 3: 'Some nights', 4: 'Most nights', 5: 'Every night'
          },
          sudden_pain: {
            1: 'No days', 2: 'Only 1 or 2 days', 3: 'Some days', 4: 'Most days', 5: 'Every day'
          },
          limping: {
            1: 'Rarely/never', 2: 'Sometimes or just at first', 3: 'Often, not just at first', 4: 'Most of the time', 5: 'All of the time'
          },
          walking_duration: {
            1: 'No pain for 30 minutes or more', 2: '16 to 30 minutes', 3: '5 to 15 minutes', 4: 'Around the house only', 5: 'Not at all'
          },
          stairs: {
            1: 'Yes, easily', 2: 'With little difficulty', 3: 'With moderate difficulty', 4: 'With extreme difficulty', 5: 'No, impossible'
          },
          socks: {
            1: 'Yes, easily', 2: 'With little difficulty', 3: 'With moderate difficulty', 4: 'With extreme difficulty', 5: 'No, impossible'
          },
          standing_up: {
            1: 'Not at all painful', 2: 'Slightly painful', 3: 'Moderately painful', 4: 'Very painful', 5: 'Unbearable'
          },
          transport: {
            1: 'No trouble at all', 2: 'Very little trouble', 3: 'Moderate trouble', 4: 'Extreme difficulty', 5: 'Impossible to do'
          },
          washing: {
            1: 'No trouble at all', 2: 'Very little trouble', 3: 'Moderate trouble', 4: 'Extreme difficulty', 5: 'Impossible to do'
          },
          shopping: {
            1: 'Yes, easily', 2: 'With little difficulty', 3: 'With moderate difficulty', 4: 'With extreme difficulty', 5: 'No, impossible'
          },
          work_interference: {
            1: 'Not at all', 2: 'A little bit', 3: 'Moderately', 4: 'Greatly', 5: 'Totally'
          }
        },
        de: {
          body_side: {
            1: 'rechts', 2: 'links', 3: 'beide'
          },
          pain: {
            1: 'Keiner', 2: 'Sehr leicht', 3: 'Leicht', 4: 'Mäßig', 5: 'Stark'
          },
          night_pain: {
            1: 'Keine Nächte', 2: 'Nur 1 oder 2 Nächte', 3: 'Einige Nächte', 4: 'Die meisten Nächte', 5: 'Jede Nacht'
          },
          sudden_pain: {
            1: 'Keine Tage', 2: 'Nur 1 oder 2 Tage', 3: 'Einige Tage', 4: 'Die meisten Tage', 5: 'Jeden Tag'
          },
          limping: {
            1: 'Selten/nie', 2: 'Manchmal oder nur am Anfang', 3: 'Oft, nicht nur am Anfang', 4: 'Die meiste Zeit', 5: 'Die ganze Zeit'
          },
          walking_duration: {
            1: 'Kein Schmerz für 30 Minuten oder mehr', 2: '16 bis 30 Minuten', 3: '5 bis 15 Minuten', 4: 'Nur im Haus', 5: 'Überhaupt nicht'
          },
          stairs: {
            1: 'Ja, leicht', 2: 'Mit wenig Schwierigkeit', 3: 'Mit mäßiger Schwierigkeit', 4: 'Mit extremer Schwierigkeit', 5: 'Nein, unmöglich'
          },
          socks: {
            1: 'Ja, leicht', 2: 'Mit wenig Schwierigkeit', 3: 'Mit mäßiger Schwierigkeit', 4: 'Mit extremer Schwierigkeit', 5: 'Nein, unmöglich'
          },
          standing_up: {
            1: 'Überhaupt nicht schmerzhaft', 2: 'Leicht schmerzhaft', 3: 'Mäßig schmerzhaft', 4: 'Sehr schmerzhaft', 5: 'Unerträglich'
          },
          transport: {
            1: 'Überhaupt keine Probleme', 2: 'Sehr wenig Probleme', 3: 'Mäßige Probleme', 4: 'Extreme Schwierigkeiten', 5: 'Unmöglich'
          },
          washing: {
            1: 'Überhaupt keine Probleme', 2: 'Sehr wenig Probleme', 3: 'Mäßige Probleme', 4: 'Extreme Schwierigkeiten', 5: 'Unmöglich'
          },
          shopping: {
            1: 'Ja, leicht', 2: 'Mit wenig Schwierigkeit', 3: 'Mit mäßiger Schwierigkeit', 4: 'Mit extremer Schwierigkeit', 5: 'Nein, unmöglich'
          },
          work_interference: {
            1: 'Überhaupt nicht', 2: 'Ein bisschen', 3: 'Mäßig', 4: 'Sehr', 5: 'Vollständig'
          }
        }
      };

      console.log('Traitement des données OHS...');
      console.log('Nombre de réponses OHS à traiter:', ohsData.length);
      
      ohsData.forEach((response, index) => {
        console.log(`Traitement réponse ${index + 1}:`, response);
        try {
          const responses = JSON.parse(response.responses);
          console.log('Réponses parsées:', responses);
          const language = response.language || 'en';
          const questions = ohsQuestions[language] || ohsQuestions.en;
          const options = ohsOptions[language] || ohsOptions.en;
          
          // Déterminer la phase et le jour à partir du taskId
          const phase = response.taskId === 'day24-ohs' ? 'Final Step' : 'Buchinger Fasting';
          const dayNumber = response.taskId === 'day24-ohs' ? 24 : 1; // OHS jour 1 ou jour 24
          
          Object.entries(responses).forEach(([questionId, answerData]) => {
            // Gérer la nouvelle structure avec {value, label} ou l'ancienne structure
            let answer, answerLabel;
            if (typeof answerData === 'object' && answerData.value !== undefined) {
              // Nouvelle structure avec labels
              answer = answerData.value;
              answerLabel = answerData.label;
            } else {
              // Ancienne structure (fallback)
              answer = answerData;
              answerLabel = options[questionId] && options[questionId][answer] 
                ? options[questionId][answer] 
                : `Option ${answer}`;
            }
            
            const questionText = questions[questionId] || questionId;
            
            // Recodification des réponses selon le système standard OHS (0=meilleur, 4=pire)
            // Toutes les questions OHS ont 5 options (1-5) selon la version officielle
            let recodedAnswer;
            // 1=meilleur → 0, 2 → 1, 3 → 2, 4 → 3, 5=pire → 4
            recodedAnswer = answer - 1; // 1→0, 2→1, 3→2, 4→3, 5→4
            
            const formattedRow = [
              response.patientId,
              phase,
              dayNumber,
              new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }),
              response.version || 1, // Version de l'entrée
              questionIdMapping[questionId] || questionId, // Question ID (Q1, Q2, etc.)
              questionText,
              answer, // Réponse originale
              recodedAnswer, // Réponse recodifiée
              answerLabel,
              language // Langue utilisée
            ];
            console.log('Ligne formatée:', formattedRow);
            ohsDataFormatted.push(formattedRow);
          });
        } catch (error) {
          console.error('Erreur parsing OHS response:', error);
        }
      });
      
      console.log('Données OHS finales formatées:', ohsDataFormatted);
      console.log('Nombre de lignes OHS formatées:', ohsDataFormatted.length);

      // Traitement des données SF-36
      console.log('=== DÉBUT TRAITEMENT SF-36 ===');
      console.log('Nombre de réponses SF-36 à traiter:', sf36Data.length);
      console.log('Données SF36 brutes:', sf36Data);
      
      sf36Data.forEach((response, index) => {
        console.log(`Traitement réponse SF-36 ${index + 1}:`, response);
        console.log('Version de la réponse:', response.version);
        console.log('Patient ID:', response.patientId);
        console.log('Task ID:', response.taskId);
        try {
          const responses = JSON.parse(response.responses);
          const scores = JSON.parse(response.score);
          console.log('Réponses parsées:', responses);
          console.log('Scores parsés:', scores);
          const language = response.language || 'en';
          
          // Déterminer la phase et le jour à partir du taskId
          const phase = response.taskId === 'day24-sf36' ? 'Final Step' : 'Buchinger Fasting';
          const dayNumber = response.taskId === 'day24-sf36' ? 24 : 1; // SF-36 jour 1 ou jour 24
          
          // Questions SF-36 avec leurs labels en allemand
          const sf36Questions = {
            de: {
              // Q1-Q2: Questions générales sur la santé
              gh1: 'Wie würden Sie Ihren Gesundheitszustand im Allgemeinen beschreiben?',
              gh2: 'Im Vergleich zum vergangenen Jahr, wie würden Sie Ihren derzeitigen Gesundheitszustand beschreiben?',
              
              // Q3: Physical Functioning (10 sous-questions)
              pf1: 'a. anstrengende Tätigkeiten, z.B. schnell laufen, schwere Gegenstände heben, anstrengenden Sport treiben',
              pf2: 'b. mittelschwere Tätigkeiten, z.B. einen Tisch verschieben, staubsaugen, kegeln, Golf spielen',
              pf3: 'c. Einkaufstaschen heben oder tragen',
              pf4: 'd. mehrere Treppenabsätze steigen',
              pf5: 'e. einen Treppenabsatz steigen',
              pf6: 'f. sich beugen, knien, bücken',
              pf7: 'g. mehr als 1 Kilometer zu Fuß gehen',
              pf8: 'h. mehrere Straßenkreuzungen weit zu Fuß gehen',
              pf9: 'i. eine Straßenkreuzung weit zu Fuß gehen',
              pf10: 'j. sich baden oder anziehen',
              
              // Q4: Role Physical (4 sous-questions)
              rp11: 'a. Ich konnte nicht so lange wie üblich tätig sein',
              rp12: 'b. Ich habe weniger geschafft als ich wollte',
              rp13: 'c. Ich konnte nur bestimmte Dinge tun',
              rp14: 'd. Ich hatte Schwierigkeiten bei der Ausführung (z.B. ich musste mich besonders anstrengen)',
              
              // Q5: Role Emotional (3 sous-questions)
              re11: 'a. Ich konnte nicht so lange wie üblich tätig sein',
              re12: 'b. Ich habe weniger geschafft als ich wollte',
              re13: 'c. Ich konnte nicht so sorgfältig wie üblich arbeiten',
              
              // Q6: Social Functioning
              sf1: 'Wie sehr haben Ihre körperliche Gesundheit oder seelischen Probleme in den vergangenen 4 Wochen Ihre normalen Kontakte zu Familienangehörigen, Freunden, Nachbarn oder zum Bekanntenkreis beeinträchtigt?',
              
              // Q7: Bodily Pain
              bp1: 'Wie stark waren Ihre Schmerzen in den vergangenen 4 Wochen?',
              
              // Q8: Bodily Pain (interférence)
              bp2: 'Inwieweit haben die Schmerzen Sie in den vergangenen 4 Wochen bei der Ausübung Ihrer Alltagstätigkeiten zu Hause und im Beruf behindert?',
              
              // Q9: Social Functioning (fréquence)
              sf2: 'Wie häufig haben Ihre körperliche Gesundheit oder seelischen Probleme in den vergangenen 4 Wochen Ihre Kontakte zu anderen Menschen (Besuche bei Freunden, Verwandten usw.) beeinträchtigt?',
              
              // Q10: General Health (4 sous-questions)
              gh3: 'a. Ich scheine etwas leichter als andere krank zu werden',
              gh4: 'b. Ich bin genauso gesund wie alle anderen, die ich kenne',
              gh5: 'c. Ich erwarte, daß meine Gesundheit nachläßt',
              gh6: 'd. Ich erfreue mich ausgezeichneter Gesundheit',
              
              // Q11: General Health (évaluation actuelle)
              gh7: 'Wie würden Sie Ihren derzeitigen Gesundheitszustand beschreiben?',
              
              // Q12: Mental Health (38 sous-questions)
              mh11: 'a. Ich bin andauernd müde',
              mh12: 'b. Ich habe nachts Schmerzen',
              mh13: 'c. Ich fühle mich niedergeschlagen',
              mh14: 'd. Ich habe unerträgliche Schmerzen',
              mh15: 'e. Ich nehme Tabletten, um schlafen zu können',
              mh16: 'f. Ich habe vergessen, wie es ist Freude zu empfinden',
              mh17: 'g. Ich fühle mich gereizt',
              mh18: 'h. Ich finde es schmerzhaft, meine Körperposition zu verändern',
              mh19: 'i. Ich fühle mich einsam',
              mh110: 'j. Ich kann mich nur innerhalb des Hauses bewegen',
              mh111: 'k. Es fällt mir schwer mich zu bücken',
              mh112: 'l. Alles strengt mich an',
              mh113: 'm. Ich wache in den frühen Morgenstunden auf',
              mh114: 'n. Ich kann überhaupt nicht gehen',
              mh115: 'o. Es fällt mir schwer, zu anderen Menschen Kontakt aufzunehmen',
              mh116: 'p. Die Tage ziehen sich',
              mh117: 'q. Ich habe Schwierigkeiten Treppen hinauf- und hinunterzugehen',
              mh118: 'r. Es fällt mir schwer nach Gegenständen zu greifen',
              mh119: 's. Ich habe Schmerzen beim Gehen',
              mh120: 't. Mir reißt derzeit oft der Geduldsfaden',
              mh121: 'u. Ich fühle, daß ich niemanden nahestehe',
              mh122: 'v. Ich liege nachts die meiste Zeit wach',
              mh123: 'w. Ich habe das Gefühl, die Kontrolle zu verlieren',
              mh124: 'x. Ich habe Schmerzen, wenn ich stehe',
              mh125: 'y. Es fällt mir schwer mich selbst anzuziehen',
              mh126: 'z. Meine Energie läßt schnell nach',
              mh127: 'aa. Es fällt mir schwer lange zu stehen (z.B. am Spülbecken, an der Bushaltestelle)',
              mh128: 'ab. Ich habe andauernd Schmerzen',
              mh129: 'ac. Ich brauche lange zum Einschlafen',
              mh130: 'ad. Ich habe das Gefühl für andere Menschen eine Last zu sein',
              mh131: 'ae. Sorgen halten mich nachts wach',
              mh132: 'af. Ich fühle, daß das Leben nicht lebenswert ist',
              mh133: 'ag. Ich schlafe nachts schlecht',
              mh134: 'ah. Es fällt mir schwer mit anderen Menschen auszukommen',
              mh135: 'ai. Ich brauche Hilfe, wenn ich mich außer Haus bewegen will (Stock oder jemand, der mich stützt)',
              mh136: 'aj. Ich habe Schmerzen, wenn ich Treppen hinauf- und hinuntergehe',
              mh137: 'ak. Ich wache deprimiert auf',
              mh138: 'al. Ich habe Schmerzen, wenn ich sitze'
            },
            en: {
              // Questions en anglais (pour référence)
              gh1: 'How would you describe your general health?',
              gh2: 'Compared to one year ago, how would you rate your health in general now?',
              // ... autres questions en anglais
            }
          };

          // Options de réponses SF-36 en allemand
          const sf36Options = {
            de: {
              // Q3: Physical Functioning (10 questions)
              pf1: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf2: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf3: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf4: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf5: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf6: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf7: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf8: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf9: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              pf10: { 1: 'Ja, stark eingeschränkt', 2: 'Ja, etwas eingeschränkt', 3: 'Nein, überhaupt nicht eingeschränkt' },
              
              // Q4: Role Physical (4 questions)
              rp11: { 1: 'Ja', 2: 'Nein' },
              rp12: { 1: 'Ja', 2: 'Nein' },
              rp13: { 1: 'Ja', 2: 'Nein' },
              rp14: { 1: 'Ja', 2: 'Nein' },
              
              // Q5: Role Emotional (3 questions)
              re11: { 1: 'Ja', 2: 'Nein' },
              re12: { 1: 'Ja', 2: 'Nein' },
              re13: { 1: 'Ja', 2: 'Nein' },
              
              // Q6: Social Functioning
              sf1: { 1: 'Überhaupt nicht', 2: 'Etwas', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
              
              // Q7: Bodily Pain
              bp1: { 1: 'Ich hatte keine Schmerzen', 2: 'Sehr leicht', 3: 'Leicht', 4: 'Mäßig', 5: 'Stark', 6: 'Sehr stark' },
              
              // Q8: Bodily Pain (interférence)
              bp2: { 1: 'Überhaupt nicht', 2: 'Ein bißchen', 3: 'Mäßig', 4: 'Ziemlich', 5: 'Sehr' },
              
              // Q9: Social Functioning (fréquence)
              sf2: { 1: 'Immer', 2: 'Meistens', 3: 'Manchmal', 4: 'Selten', 5: 'Nie' },
              
              // Q1-Q2, Q10-Q11: General Health
              gh1: { 1: 'Ausgezeichnet', 2: 'Sehr gut', 3: 'Gut', 4: 'Weniger gut', 5: 'Schlecht' },
              gh2: { 1: 'Derzeit viel besser als vor einem Jahr', 2: 'Derzeit etwas besser als vor einem Jahr', 3: 'Etwa so wie vor einem Jahr', 4: 'Derzeit etwas schlechter als vor einem Jahr', 5: 'Derzeit viel schlechter als vor einem Jahr' },
              gh3: { 1: 'Trifft ganz zu', 2: 'Trifft weitgehend zu', 3: 'Weiß nicht', 4: 'Trifft weitgehend nicht zu', 5: 'Trifft überhaupt nicht zu' },
              gh4: { 1: 'Trifft ganz zu', 2: 'Trifft weitgehend zu', 3: 'Weiß nicht', 4: 'Trifft weitgehend nicht zu', 5: 'Trifft überhaupt nicht zu' },
              gh5: { 1: 'Trifft ganz zu', 2: 'Trifft weitgehend zu', 3: 'Weiß nicht', 4: 'Trifft weitgehend nicht zu', 5: 'Trifft überhaupt nicht zu' },
              gh6: { 1: 'Trifft ganz zu', 2: 'Trifft weitgehend zu', 3: 'Weiß nicht', 4: 'Trifft weitgehend nicht zu', 5: 'Trifft überhaupt nicht zu' },
              gh7: { 1: 'sehr gut', 2: 'gut', 3: 'mittelmäßig', 4: 'schlecht', 5: 'sehr schlecht' },
              
              // Q12: Mental Health (38 questions)
              mh11: { 1: 'Ja', 2: 'Nein' },
              mh12: { 1: 'Ja', 2: 'Nein' },
              mh13: { 1: 'Ja', 2: 'Nein' },
              mh14: { 1: 'Ja', 2: 'Nein' },
              mh15: { 1: 'Ja', 2: 'Nein' },
              mh16: { 1: 'Ja', 2: 'Nein' },
              mh17: { 1: 'Ja', 2: 'Nein' },
              mh18: { 1: 'Ja', 2: 'Nein' },
              mh19: { 1: 'Ja', 2: 'Nein' },
              mh110: { 1: 'Ja', 2: 'Nein' },
              mh111: { 1: 'Ja', 2: 'Nein' },
              mh112: { 1: 'Ja', 2: 'Nein' },
              mh113: { 1: 'Ja', 2: 'Nein' },
              mh114: { 1: 'Ja', 2: 'Nein' },
              mh115: { 1: 'Ja', 2: 'Nein' },
              mh116: { 1: 'Ja', 2: 'Nein' },
              mh117: { 1: 'Ja', 2: 'Nein' },
              mh118: { 1: 'Ja', 2: 'Nein' },
              mh119: { 1: 'Ja', 2: 'Nein' },
              mh120: { 1: 'Ja', 2: 'Nein' },
              mh121: { 1: 'Ja', 2: 'Nein' },
              mh122: { 1: 'Ja', 2: 'Nein' },
              mh123: { 1: 'Ja', 2: 'Nein' },
              mh124: { 1: 'Ja', 2: 'Nein' },
              mh125: { 1: 'Ja', 2: 'Nein' },
              mh126: { 1: 'Ja', 2: 'Nein' },
              mh127: { 1: 'Ja', 2: 'Nein' },
              mh128: { 1: 'Ja', 2: 'Nein' },
              mh129: { 1: 'Ja', 2: 'Nein' },
              mh130: { 1: 'Ja', 2: 'Nein' },
              mh131: { 1: 'Ja', 2: 'Nein' },
              mh132: { 1: 'Ja', 2: 'Nein' },
              mh133: { 1: 'Ja', 2: 'Nein' },
              mh134: { 1: 'Ja', 2: 'Nein' },
              mh135: { 1: 'Ja', 2: 'Nein' },
              mh136: { 1: 'Ja', 2: 'Nein' },
              mh137: { 1: 'Ja', 2: 'Nein' },
              mh138: { 1: 'Ja', 2: 'Nein' }
            },
            en: {
              // Options en anglais (pour référence)
              pf1: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf2: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf3: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf4: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf5: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf6: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf7: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf8: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf9: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              pf10: { 1: 'Yes, limited a lot', 2: 'Yes, limited a little', 3: 'No, not limited at all' },
              rp11: { 1: 'Yes', 2: 'No' },
              rp12: { 1: 'Yes', 2: 'No' },
              rp13: { 1: 'Yes', 2: 'No' },
              rp14: { 1: 'Yes', 2: 'No' },
              re11: { 1: 'Yes', 2: 'No' },
              re12: { 1: 'Yes', 2: 'No' },
              re13: { 1: 'Yes', 2: 'No' },
              bp1: { 1: 'None', 2: 'Very mild', 3: 'Mild', 4: 'Moderate', 5: 'Severe', 6: 'Very severe' },
              bp2: { 1: 'Not at all', 2: 'A little bit', 3: 'Moderately', 4: 'Quite a bit', 5: 'Extremely' },
              sf1: { 1: 'Not at all', 2: 'Slightly', 3: 'Moderately', 4: 'Quite a bit', 5: 'Extremely' },
              sf2: { 1: 'All of the time', 2: 'Most of the time', 3: 'Some of the time', 4: 'A little of the time', 5: 'None of the time' },
              gh1: { 1: 'Excellent', 2: 'Very good', 3: 'Good', 4: 'Fair', 5: 'Poor' },
              gh2: { 1: 'Much better now than one year ago', 2: 'Somewhat better now than one year ago', 3: 'About the same as one year ago', 4: 'Somewhat worse now than one year ago', 5: 'Much worse now than one year ago' },
              gh3: { 1: 'Definitely true', 2: 'Mostly true', 3: 'Don\'t know', 4: 'Mostly false', 5: 'Definitely false' },
              gh4: { 1: 'Definitely true', 2: 'Mostly true', 3: 'Don\'t know', 4: 'Mostly false', 5: 'Definitely false' },
              gh5: { 1: 'Definitely true', 2: 'Mostly true', 3: 'Don\'t know', 4: 'Mostly false', 5: 'Definitely false' },
              gh6: { 1: 'Definitely true', 2: 'Mostly true', 3: 'Don\'t know', 4: 'Mostly false', 5: 'Definitely false' },
              gh7: { 1: 'Very good', 2: 'Good', 3: 'Fair', 4: 'Poor', 5: 'Very poor' },
              mh11: { 1: 'Yes', 2: 'No' },
              mh12: { 1: 'Yes', 2: 'No' },
              mh13: { 1: 'Yes', 2: 'No' },
              mh14: { 1: 'Yes', 2: 'No' },
              mh15: { 1: 'Yes', 2: 'No' },
              mh16: { 1: 'Yes', 2: 'No' },
              mh17: { 1: 'Yes', 2: 'No' },
              mh18: { 1: 'Yes', 2: 'Nein' },
              mh19: { 1: 'Yes', 2: 'No' },
              mh110: { 1: 'Yes', 2: 'No' },
              mh111: { 1: 'Yes', 2: 'No' },
              mh112: { 1: 'Yes', 2: 'No' },
              mh113: { 1: 'Yes', 2: 'No' },
              mh114: { 1: 'Yes', 2: 'No' },
              mh115: { 1: 'Yes', 2: 'No' },
              mh116: { 1: 'Yes', 2: 'No' },
              mh117: { 1: 'Yes', 2: 'No' },
              mh118: { 1: 'Yes', 2: 'No' },
              mh119: { 1: 'Yes', 2: 'No' },
              mh120: { 1: 'Yes', 2: 'No' },
              mh121: { 1: 'Yes', 2: 'No' },
              mh122: { 1: 'Yes', 2: 'No' },
              mh123: { 1: 'Yes', 2: 'No' },
              mh124: { 1: 'Yes', 2: 'No' },
              mh125: { 1: 'Yes', 2: 'No' },
              mh126: { 1: 'Yes', 2: 'No' },
              mh127: { 1: 'Yes', 2: 'No' },
              mh128: { 1: 'Yes', 2: 'No' },
              mh129: { 1: 'Yes', 2: 'No' },
              mh130: { 1: 'Yes', 2: 'No' },
              mh131: { 1: 'Yes', 2: 'No' },
              mh132: { 1: 'Yes', 2: 'No' },
              mh133: { 1: 'Yes', 2: 'No' },
              mh134: { 1: 'Yes', 2: 'No' },
              mh135: { 1: 'Yes', 2: 'No' },
              mh136: { 1: 'Yes', 2: 'No' },
              mh137: { 1: 'Yes', 2: 'No' },
              mh138: { 1: 'Yes', 2: 'No' }
            }
          };

          const questions = sf36Questions[language] || sf36Questions.de;
          const options = sf36Options[language] || sf36Options.de;
          
          // Mapping simple des IDs de questions SF-36 vers Q1, Q2, Q3a, etc.
          const questionIdMapping = {
            // Q1-Q2: Questions générales sur la santé
            gh1: 'Q1',
            gh2: 'Q2',
            
            // Q3: Physical Functioning (10 sous-questions)
            pf1: 'Q3a', pf2: 'Q3b', pf3: 'Q3c', pf4: 'Q3d', pf5: 'Q3e',
            pf6: 'Q3f', pf7: 'Q3g', pf8: 'Q3h', pf9: 'Q3i', pf10: 'Q3j',
            
            // Q4: Role Physical (4 sous-questions)
            rp11: 'Q4a', rp12: 'Q4b', rp13: 'Q4c', rp14: 'Q4d',
            
            // Q5: Role Emotional (3 sous-questions)
            re11: 'Q5a', re12: 'Q5b', re13: 'Q5c',
            
            // Q6: Social Functioning
            sf1: 'Q6',
            
            // Q7: Bodily Pain
            bp1: 'Q7',
            
            // Q8: Bodily Pain (interférence)
            bp2: 'Q8',
            
            // Q9: Social Functioning (fréquence)
            sf2: 'Q9',
            
            // Q10: General Health (4 sous-questions)
            gh3: 'Q10a', gh4: 'Q10b', gh5: 'Q10c', gh6: 'Q10d',
            
            // Q11: General Health (évaluation actuelle)
            gh7: 'Q11',
            
            // Q12: Mental Health (38 sous-questions)
            mh11: 'Q12a', mh12: 'Q12b', mh13: 'Q12c', mh14: 'Q12d', mh15: 'Q12e',
            mh16: 'Q12f', mh17: 'Q12g', mh18: 'Q12h', mh19: 'Q12i', mh110: 'Q12j',
            mh111: 'Q12k', mh112: 'Q12l', mh113: 'Q12m', mh114: 'Q12n', mh115: 'Q12o',
            mh116: 'Q12p', mh117: 'Q12q', mh118: 'Q12r', mh119: 'Q12s', mh120: 'Q12t',
            mh121: 'Q12u', mh122: 'Q12v', mh123: 'Q12w', mh124: 'Q12x', mh125: 'Q12y',
            mh126: 'Q12z', mh127: 'Q12aa', mh128: 'Q12ab', mh129: 'Q12ac', mh130: 'Q12ad',
            mh131: 'Q12ae', mh132: 'Q12af', mh133: 'Q12ag', mh134: 'Q12ah', mh135: 'Q12ai',
            mh136: 'Q12aj', mh137: 'Q12ak', mh138: 'Q12al'
          };
          
          // Mapping des domaines SF-36 (basé sur les IDs réels de la BDD)
          const domainMapping = {
            // Physical Functioning (PF)
            pf1: 'PF', pf2: 'PF', pf3: 'PF', pf4: 'PF', pf5: 'PF', pf6: 'PF', pf7: 'PF', pf8: 'PF', pf9: 'PF', pf10: 'PF',
            
            // Role Physical (RP)
            rp11: 'RP', rp12: 'RP', rp13: 'RP', rp14: 'RP',
            
            // Role Emotional (RE)
            re11: 'RE', re12: 'RE', re13: 'RE',
            
            // Bodily Pain (BP)
            bp1: 'BP', bp2: 'BP',
            
            // General Health (GH)
            gh1: 'GH', gh2: 'GH', gh3: 'GH', gh4: 'GH', gh5: 'GH', gh7: 'GH',
            
            // Social Functioning (SF)
            sf1: 'SF', sf2: 'SF',
            
            // Mental Health (MH)
            mh11: 'MH', mh12: 'MH', mh13: 'MH', mh14: 'MH', mh15: 'MH', mh16: 'MH', mh17: 'MH', mh18: 'MH', mh19: 'MH', mh110: 'MH',
            mh111: 'MH', mh112: 'MH', mh113: 'MH', mh114: 'MH', mh115: 'MH', mh116: 'MH', mh117: 'MH', mh118: 'MH', mh119: 'MH', mh120: 'MH',
            mh121: 'MH', mh122: 'MH', mh123: 'MH', mh124: 'MH', mh125: 'MH', mh126: 'MH', mh127: 'MH', mh128: 'MH', mh129: 'MH', mh130: 'MH',
            mh131: 'MH', mh132: 'MH', mh133: 'MH', mh134: 'MH', mh135: 'MH', mh136: 'MH', mh137: 'MH', mh138: 'MH'
          };

          // Mapping pour déterminer si une question doit être inversée
          // True = la réponse doit être inversée (0 = meilleur, valeurs élevées = pire)
          // False = pas d'inversion nécessaire (valeurs élevées = meilleur)
          const inverseMapping = {
            // Physical Functioning (PF) - pas d'inversion nécessaire
            pf1: false, pf2: false, pf3: false, pf4: false, pf5: false,
            pf6: false, pf7: false, pf8: false, pf9: false, pf10: false,
            
            // Role Physical (RP) - pas d'inversion nécessaire
            rp1: false, rp2: false, rp3: false, rp4: false,
            
            // Bodily Pain (BP) - inversion nécessaire
            bp1: true,  // 1=None (meilleur) → 6=Very severe (pire)
            bp2: true,  // 1=Not at all (meilleur) → 5=Extremely (pire)
            
            // General Health (GH) - inversion nécessaire pour certaines questions
            gh1: true,  // 1=Excellent (meilleur) → 5=Poor (pire)
            gh2: true,  // 1=Much better (meilleur) → 5=Much worse (pire)
            gh3: true,  // 1=Definitely true (pire) → 5=Definitely false (meilleur)
            gh4: false, // 1=Definitely true (meilleur) → 5=Definitely false (pire)
            gh5: true,  // 1=Definitely true (pire) → 5=Definitely false (meilleur)
            gh6: false, // 1=Definitely true (meilleur) → 5=Definitely false (pire)
            gh7: true,  // 1=Excellent (meilleur) → 5=Poor (pire)
            
            // Social Functioning (SF) - inversion nécessaire
            sf1: true,  // 1=All of the time (pire) → 5=None of the time (meilleur)
            sf2: true,  // 1=Not at all (meilleur) → 5=Extremely (pire)
            
            // Role Emotional (RE) - pas d'inversion nécessaire
            re11: false, re12: false, re13: false,
            
            // Mental Health (MH) - inversion nécessaire pour certaines questions
            mh11: true, mh12: true, mh13: false, mh14: true, mh15: false,
            mh16: true, mh17: false, mh18: true, mh19: false, mh110: true,
            mh111: false, mh112: true, mh113: false, mh114: true, mh115: false,
            mh116: true, mh117: false, mh118: true, mh119: false, mh120: true,
            mh121: false, mh122: true, mh123: false, mh124: true, mh125: false,
            mh126: true, mh127: false, mh128: true, mh129: false, mh130: true,
            mh131: false, mh132: true, mh133: false, mh134: true, mh135: false,
            mh136: true, mh137: false, mh138: true
          };
          
          Object.entries(responses).forEach(([questionId, answerData]) => {
            // Gérer la nouvelle structure avec {value, label} ou l'ancienne structure
            let answer, answerLabel;
            if (typeof answerData === 'object' && answerData.value !== undefined) {
              // Nouvelle structure avec labels
              answer = answerData.value;
              answerLabel = answerData.label;
            } else {
              // Ancienne structure (fallback)
              answer = answerData;
              answerLabel = options[questionId] && options[questionId][answer] 
                ? options[questionId][answer] 
                : `Option ${answer}`;
            }
            
            const questionText = questions[questionId] || questionId;
            
            // Debug: vérifier les options disponibles
            console.log(`SF-36 Debug - questionId: ${questionId}, answer: ${answer}, options:`, options[questionId]);
            console.log(`SF-36 Debug - answerLabel before: ${answerLabel}`);
            
            // S'assurer que answerLabel utilise les options en allemand
            if (options[questionId] && options[questionId][answer]) {
              answerLabel = options[questionId][answer];
            }
            console.log(`SF-36 Debug - answerLabel after: ${answerLabel}`);
            
            // Calculer le nombre de réponses possibles pour cette question
            const numberOfOptions = options[questionId] ? Object.keys(options[questionId]).length : 0;
            
            // Déterminer si la question doit être inversée
            const toInverse = inverseMapping[questionId] || false;
            
            const formattedRow = [
              response.patientId,
              phase,
              dayNumber,
              new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }),
              response.version || 1, // Ajouter la version
              questionIdMapping[questionId] || questionId, // Utiliser le mapping simplifié
              questionText,
              answer,
              answerLabel,
              numberOfOptions,
              toInverse,
              language // Langue utilisée
            ];
            console.log('Ligne SF-36 formatée:', formattedRow);
            sf36DataFormatted.push(formattedRow);
          });
        } catch (error) {
          console.error(`Erreur traitement réponse SF-36 ${index + 1}:`, error);
        }
      });

      console.log('=== FIN TRAITEMENT SF-36 ===');
      console.log('Données SF-36 finales formatées:', sf36DataFormatted);
      console.log('Nombre de lignes SF-36 formatées:', sf36DataFormatted.length);

      // Traitement des données QoR-15
      console.log('=== DÉBUT TRAITEMENT QoR-15 ===');
      const qor15DataFiltered = qor15Data.filter(response => response.questionnaireType === 'QoR15');
      const qor15DataFormatted = [
        ['Patient ID', 'Phase', 'Day', 'Completion Date', 'Version', 'Question ID', 'Question Text', 'Original Answer', 'Recoded Answer', 'Answer Label', 'Number of Options', 'To Inverse', 'Language']
      ];

      const qor15Questions = {
        en: {
          q1: 'Able to breathe easily',
          q2: 'Have had a good sleep',
          q3: 'Been able to enjoy food',
          q4: 'Feeling rested',
          q5: 'Able to look after personal toilet and hygiene unaided',
          q6: 'Able to communicate with family or friends',
          q7: 'Getting support from hospital doctors and nurses',
          q8: 'Able to return to work or usual home activities',
          q9: 'Moderate pain',
          q10: 'Severe pain',
          q11: 'Nausea',
          q12: 'Vomiting',
          q13: 'Feeling worried or anxious',
          q14: 'Feeling sad or depressed',
          q15: 'Feeling angry or irritable'
        },
        de: {
          q1: 'Konnten Sie frei und problemlos atmen?',
          q2: 'Haben Sie gut geschlafen?',
          q3: 'Konnten Sie das Essen genießen?',
          q4: 'Fühlten Sie sich ausgeruht?',
          q5: 'Konnten Sie sich eigenständig um Ihre Körperpflege/Toilette kümmern?',
          q6: 'Konnten Sie mit Familie oder Freunden kommunizieren?',
          q7: 'Haben Sie Unterstützung durch das Krankenhauspersonal erhalten?',
          q8: 'Waren Sie in der Lage, zu Ihrer Arbeit oder Ihren üblichen häuslichen Aktivitäten zurückzukehren?',
          q9: 'Hatten Sie mäßige Schmerzen?',
          q10: 'Hatten Sie starke Schmerzen?',
          q11: 'Hatten Sie Übelkeit?',
          q12: 'Mussten Sie sich übergeben (Erbrechen)?',
          q13: 'Fühlten Sie sich ängstlich oder besorgt?',
          q14: 'Fühlten Sie sich traurig oder niedergeschlagen?',
          q15: 'Fühlten Sie sich wütend oder gereizt?'
        }
      };

      const qor15Options = {
        en: {
          0: '0 - Never/Very poor',
          1: '1',
          2: '2',
          3: '3',
          4: '4',
          5: '5',
          6: '6',
          7: '7',
          8: '8',
          9: '9',
          10: '10 - Always/Very good'
        },
        de: {
          0: '0 - Nie/Sehr schlecht',
          1: '1',
          2: '2',
          3: '3',
          4: '4',
          5: '5',
          6: '6',
          7: '7',
          8: '8',
          9: '9',
          10: '10 - Immer/Sehr gut'
        }
      };

      console.log('Traitement des données QoR-15...');
      console.log('Nombre de réponses QoR-15 à traiter:', qor15DataFiltered.length);
      
      qor15DataFiltered.forEach((response, index) => {
        console.log(`Traitement réponse QoR-15 ${index + 1}:`, response);
        try {
          const responses = JSON.parse(response.responses);
          console.log('Réponses QoR-15 parsées:', responses);
          const language = response.language || 'en';
          const questions = qor15Questions[language] || qor15Questions.en;
          const options = qor15Options[language] || qor15Options.en;
          
          // Déterminer la phase et le jour à partir du taskId
          const phase = 'Final Step';
          const dayNumber = 24; // QoR-15 est toujours jour 24 (pas de changement nécessaire)
          
          Object.entries(responses).forEach(([questionId, answerData]) => {
            // Gérer la nouvelle structure avec {value, label} ou l'ancienne structure
            let answer, answerLabel;
            if (typeof answerData === 'object' && answerData.value !== undefined) {
              // Nouvelle structure avec labels
              answer = answerData.value;
              answerLabel = answerData.label;
            } else {
              // Ancienne structure (fallback)
              answer = answerData;
              answerLabel = options[answer] || `Value ${answer}`;
            }
            
            const questionText = questions[questionId] || questionId;
            
            // QoR-15: Questions 11-15 sont inversées (reverse scored)
            const isReverseScored = ['q11', 'q12', 'q13', 'q14', 'q15'].includes(questionId);
            const recodedAnswer = isReverseScored ? (10 - answer) : answer;
            
            const formattedRow = [
              response.patientId,
              phase,
              dayNumber,
              new Date(response.completedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: 'Europe/Paris'
              }),
              response.version || 1, // Ajouter la version
              questionIdMapping[questionId] || questionId, // Question ID (Q1, Q2, etc.)
              questionText,
              answer, // Réponse originale
              recodedAnswer, // Réponse recodifiée
              answerLabel,
              language,
              11, // QoR-15 a 11 options (0-10)
              isReverseScored // Inversion pour les questions 9-15
            ];
            console.log('Ligne QoR-15 formatée:', formattedRow);
            qor15DataFormatted.push(formattedRow);
          });
        } catch (error) {
          console.error(`Erreur traitement réponse QoR-15 ${index + 1}:`, error);
        }
      });

      console.log('=== FIN TRAITEMENT QoR-15 ===');
      console.log('Données QoR-15 finales formatées:', qor15DataFormatted);
      console.log('Nombre de lignes QoR-15 formatées:', qor15DataFormatted.length);

      console.log('Création du fichier Excel...');
      
      // Créer un workbook Excel
      const workbook = XLSX.utils.book_new();
      
      // Feuille 1: Résumé
      const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Zusammenfassung');
      
      // Feuille 2: Tâches détaillées
      const detailedWorksheet = XLSX.utils.aoa_to_sheet(detailedTasksData);
      XLSX.utils.book_append_sheet(workbook, detailedWorksheet, 'Detailed Tasks');
      
      // Feuille 3: DEGS
      let degsRows = [['Patient ID','Task ID','Completion Date','Version','Question ID','Answer','Language']];
      try {
        const allQRResp = await apiService.getAllQuestionnaireResponses();
        const allQR = allQRResp && allQRResp.success && allQRResp.data ? allQRResp.data : [];
        const degsList = allQR.filter(r => String(r.questionnaireType).toLowerCase() === 'degs');
        degsList.forEach(r => {
          try {
            const respObj = JSON.parse(r.responses || '{}');
            Object.entries(respObj).forEach(([qid, ans]) => {
              degsRows.push([
                r.patientId,
                r.taskId,
                r.completedAt ? new Date(r.completedAt).toLocaleString('fr-FR', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', timeZone:'Europe/Paris' }) : '',
                r.version || 1,
                String(qid).replace(/^Q/i, ''),
                typeof ans === 'object' ? JSON.stringify(ans) : ans,
                r.language || 'de'
              ]);
            });
          } catch {}
        });
      } catch (e) { console.error('Erreur récupération DEGS pour export:', e); }
      const degsWorksheet = XLSX.utils.aoa_to_sheet(degsRows);
      XLSX.utils.book_append_sheet(workbook, degsWorksheet, 'DEGS');

      // Feuille 4: Infection
      let infectionRows = [['Patient ID','Task ID','Completion Date','Version','Question ID','Question','Answer','Answer Label','Language']];
      try {
        const allQRResp2 = await apiService.getAllQuestionnaireResponses();
        const allQR2 = allQRResp2 && allQRResp2.success && allQRResp2.data ? allQRResp2.data : [];
        const infectionList = allQR2.filter(r => String(r.questionnaireType).toUpperCase() === 'INFECTION');
        infectionList.forEach(r => {
          try {
            const parsed = (() => { try { return typeof r.responses === 'string' ? JSON.parse(r.responses || '{}') : (r.responses || {}); } catch { return {}; } })();
            const base = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? (parsed.responses || parsed.answers || parsed)
              : parsed;
            const entries = Array.isArray(base)
              ? base.map((val, idx) => [String(idx + 1), val])
              : Object.entries(base || {});
            entries.forEach(([qid, ans]) => {
              const val = (ans && typeof ans === 'object' && 'value' in ans) ? ans.value : (typeof ans === 'object' ? JSON.stringify(ans) : ans);
              const questionText = INFECTION_QUESTIONS[qid] || qid;
              const answerLabel = INFECTION_ANSWERS[String(val).toLowerCase()] || val;
              infectionRows.push([
                r.patientId,
                r.taskId,
                r.completedAt ? new Date(r.completedAt).toLocaleString('fr-FR', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', timeZone:'Europe/Paris' }) : '',
                r.version || 1,
                qid,
                questionText,
                val,
                answerLabel,
                r.language || 'de'
              ]);
            });
          } catch {}
        });
      } catch (e) { console.error('Erreur récupération Infection pour export:', e); }
      const infectionWorksheet = XLSX.utils.aoa_to_sheet(infectionRows);
      XLSX.utils.book_append_sheet(workbook, infectionWorksheet, 'Infection');

      // Feuille 5: PROMIS-33
      let promisRows = [['Patient ID','Task ID','Completion Date','Version','Question ID','Question','Answer','Answer Label','Language']];
      try {
        const allQRResp3 = await apiService.getAllQuestionnaireResponses();
        const allQR3 = allQRResp3 && allQRResp3.success && allQRResp3.data ? allQRResp3.data : [];
        const promisList = allQR3.filter(r => String(r.questionnaireType).toUpperCase() === 'PROMIS-33');
        promisList.forEach(r => {
          try {
            const parsed = (() => { try { return typeof r.responses === 'string' ? JSON.parse(r.responses || '{}') : (r.responses || {}); } catch { return {}; } })();
            const base = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? (parsed.responses || parsed.answers || parsed)
              : parsed;
            const entries = Array.isArray(base)
              ? base.map((val, idx) => [String(idx + 1), val])
              : Object.entries(base || {});
            entries.forEach(([qid, ans]) => {
              const val = (ans && typeof ans === 'object' && 'value' in ans) ? ans.value : (typeof ans === 'object' ? JSON.stringify(ans) : ans);
              const questionText = PROMIS_QUESTIONS[qid] || qid;
              const answerLabel = PROMIS_ANSWERS[qid] && PROMIS_ANSWERS[qid][val] ? PROMIS_ANSWERS[qid][val] : val;
              promisRows.push([
                r.patientId,
                r.taskId,
                r.completedAt ? new Date(r.completedAt).toLocaleString('fr-FR', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', timeZone:'Europe/Paris' }) : '',
                r.version || 1,
                qid,
                questionText,
                val,
                answerLabel,
                r.language || 'de'
              ]);
            });
          } catch {}
        });
      } catch (e) { console.error('Erreur récupération PROMIS-33 pour export:', e); }
      const promisWorksheet = XLSX.utils.aoa_to_sheet(promisRows);
      XLSX.utils.book_append_sheet(workbook, promisWorksheet, 'PROMIS-33');

      // Feuille 6: HOOS
      let hoosRows = [['Patient ID','Task ID','Completion Date','Version','Question ID','Question','Answer','Answer Label','Language']];
      try {
        const allQRResp4 = await apiService.getAllQuestionnaireResponses();
        const allQR4 = allQRResp4 && allQRResp4.success && allQRResp4.data ? allQRResp4.data : [];
        const hoosList = allQR4.filter(r => String(r.questionnaireType).toUpperCase() === 'HOOS');
        hoosList.forEach(r => {
          try {
            const parsed = (() => { try { return typeof r.responses === 'string' ? JSON.parse(r.responses || '{}') : (r.responses || {}); } catch { return {}; } })();
            const base = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? (parsed.responses || parsed.answers || parsed)
              : parsed;
            const entries = Object.entries(base || {});
            entries.forEach(([qid, ans]) => {
              const val = (ans && typeof ans === 'object' && 'value' in ans) ? ans.value : (typeof ans === 'object' ? JSON.stringify(ans) : ans);
              const questionText = HOOS_QUESTIONS[qid] || qid;
              const answerLabel = (HOOS_ANSWERS[qid] && HOOS_ANSWERS[qid][val] !== undefined) ? HOOS_ANSWERS[qid][val] : val;
              hoosRows.push([
                r.patientId,
                r.taskId,
                r.completedAt ? new Date(r.completedAt).toLocaleString('fr-FR', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', timeZone:'Europe/Paris' }) : '',
                r.version || 1,
                qid,
                questionText,
                val,
                answerLabel,
                r.language || 'de'
              ]);
            });
          } catch {}
        });
      } catch (e) { console.error('Erreur récupération HOOS pour export:', e); }
      const hoosWorksheet = XLSX.utils.aoa_to_sheet(hoosRows);
      XLSX.utils.book_append_sheet(workbook, hoosWorksheet, 'HOOS');
      
      console.log('Feuilles créées:', workbook.SheetNames);
      
      // Générer le fichier Excel
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
      a.download = `patients_detailed_progress_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erreur export Excel:', error);
      setError(language === 'de' ? 'Fehler beim Export' : 'Error during export');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Supprimer les tokens admin
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    onLogout();
  };

  const filteredPatients = patients.filter(patient =>
    patient.patientId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-dashboard">
          <div className="admin-dashboard-header">
            <h1>👨‍⚕️ {language === 'de' ? 'Admin Dashboard' : 'Admin Dashboard'}</h1>
            <p>Loading patients...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard">
        <div className="admin-dashboard-header">
          <div className="admin-dashboard-title">
            <h1>👨‍⚕️ {language === 'de' ? 'Admin Dashboard' : 'Admin Dashboard'}</h1>
            <p>{language === 'de' ? 'Patientenverwaltung und Fortschrittsverfolgung' : 'Patient Management and Progress Tracking'}</p>
          </div>
          <div className="admin-dashboard-actions">
            <button 
              className="btn admin-btn"
              onClick={() => setShowAddForm(true)}
            >
              ➕ {language === 'de' ? 'Patient hinzufügen' : 'Add Patient'}
            </button>
            <button 
              className="btn admin-btn export-btn"
              onClick={handleExportExcel}
              disabled={loading}
            >
              {loading ? '⏳' : '📊'} {loading ? (language === 'de' ? 'Exportiere...' : 'Exporting...') : (language === 'de' ? 'Excel exportieren' : 'Export Excel')}
            </button>
            <button 
              className="btn admin-btn preview-btn"
              onClick={generateExcelPreviewData}
              disabled={loading}
            >
              👁️ {language === 'de' ? 'Excel Vorschau' : 'Excel Preview'}
            </button>
            <button 
              className="btn admin-btn logout-btn"
              onClick={handleLogout}
            >
              🚪 {language === 'de' ? 'Abmelden' : 'Logout'}
            </button>
          </div>
        </div>

        {/* Affichage des erreurs */}
        {error && (
          <div className="error admin-error">
            <span>⚠️</span>
            {error}
          </div>
        )}

        {/* Formulaire d'ajout de patient */}
        {showAddForm && (
          <div className="admin-add-form">
            <h3>{language === 'de' ? 'Neuen Patient hinzufügen' : 'Add New Patient'}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>{language === 'de' ? 'Patient ID' : 'Patient ID'}</label>
                <input
                  type="text"
                  value={newPatient.patientId}
                  onChange={(e) => setNewPatient({...newPatient, patientId: e.target.value})}
                  placeholder={language === 'de' ? 'Patient ID eingeben' : 'Enter Patient ID'}
                />
              </div>
              <div className="form-group">
                <label>{language === 'de' ? 'Passwort' : 'Password'}</label>
                <input
                  type="password"
                  value={newPatient.password}
                  onChange={(e) => setNewPatient({...newPatient, password: e.target.value})}
                  placeholder={language === 'de' ? 'Passwort eingeben' : 'Enter password'}
                />
              </div>
              <div className="form-group">
                <label>{language === 'de' ? 'Startdatum' : 'Start Date'}</label>
                <input
                  type="date"
                  value={newPatient.startDate}
                  onChange={(e) => setNewPatient({...newPatient, startDate: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>{language === 'de' ? 'Gruppe' : 'Group'}</label>
                <select
                  value={newPatient.group || 'Fasting'}
                  onChange={(e) => setNewPatient({...newPatient, group: e.target.value})}
                >
                  <option value="Fasting">Fasting</option>
                  <option value="Control">Control</option>
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn admin-btn" onClick={handleAddPatient}>
                ✅ {language === 'de' ? 'Hinzufügen' : 'Add'}
              </button>
              <button className="btn admin-btn cancel-btn" onClick={() => setShowAddForm(false)}>
                ❌ {language === 'de' ? 'Abbrechen' : 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* Barre de recherche */}
        <div className="admin-search">
          <input
            type="text"
            placeholder={language === 'de' ? 'Patienten suchen...' : 'Search patients...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Tableau des patients */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{language === 'de' ? 'Patient ID' : 'Patient ID'}</th>
                <th>{language === 'de' ? 'Startdatum' : 'Start Date'}</th>
                <th>{language === 'de' ? 'Gruppe' : 'Group'}</th>
                <th>{language === 'de' ? 'Gesamt Fortschritt' : 'Overall Progress'}</th>
                <th>{language === 'de' ? 'Status' : 'Status'}</th>
                <th>{language === 'de' ? 'Aktionen' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map((patient) => (
                <tr key={patient.patientId}>
                  <td>{patient.patientId}</td>
                  <td>
                    {editingPatient && editingPatient.patientId === patient.patientId ? (
                      <input
                        type="date"
                        value={editingPatient.startDate}
                        onChange={(e) => setEditingPatient({...editingPatient, startDate: e.target.value})}
                      />
                    ) : (
                      patient.startDate
                    )}
                  </td>
                  <td>
                    {editingPatient && editingPatient.patientId === patient.patientId ? (
                      <select
                        value={editingPatient.group || 'Fasting'}
                        onChange={(e) => setEditingPatient({...editingPatient, group: e.target.value})}
                      >
                        <option value="Fasting">Fasting</option>
                        <option value="Control">Control</option>
                      </select>
                    ) : (
                      <span>{patient.group || 'Fasting'}</span>
                    )}
                  </td>

                  <td>
                    <div className="progress-gauge-cell">
                      <div className="progress-gauge-wrapper">
                        <svg width="60" height="60" viewBox="0 0 60 60">
                          {/* Cercle de fond */}
                          <circle
                            cx="30"
                            cy="30"
                            r="25"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="4"
                          />
                          {/* Jauge attendue (couleur variable selon l'écart) */}
                          <circle
                            cx="30"
                            cy="30"
                            r="25"
                            fill="none"
                            stroke={getExpectedColor(patient)}
                            strokeWidth="3"
                            strokeDasharray={`${2 * Math.PI * 25}`}
                            strokeDashoffset={`${2 * Math.PI * 25 * (1 - (patient.expectedProgress || 0) / 100)}`}
                            transform="rotate(-90 30 30)"
                          />
                          {/* Jauge réelle (avant-plan, couleur selon retard) */}
                          <circle
                            cx="30"
                            cy="30"
                            r="25"
                            fill="none"
                            stroke={(() => {
                              const color = getDelayColor(getDelayLevel(patient));
                              console.log(`Patient ${patient.patientId} final color: ${color}`);
                              return color;
                            })()}
                            strokeWidth="5"
                            strokeDasharray={`${2 * Math.PI * 25}`}
                            strokeDashoffset={`${2 * Math.PI * 25 * (1 - (patient.overallProgress || 0) / 100)}`}
                            transform="rotate(-90 30 30)"
                          />
                          {/* Texte au centre du cercle */}
                          <text
                            x="30"
                            y="35"
                            textAnchor="middle"
                            fontSize="12"
                            fontWeight="600"
                            fill="#1e293b"
                          >
                            {patient.overallProgress || 0}%
                          </text>
                        </svg>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${patient.overallProgress === 100 ? 'completed' : 'active'}`}>
                      {patient.overallProgress === 100 ? (
                  <>
                    <img src="/images/checked.png" alt="Completed" className="status-icon" /> Completed
                  </>
                ) : (
                  <>
                    <img src="/images/pending.png" alt="Active" className="status-icon" /> Active
                  </>
                )}
                    </span>
                  </td>
                  <td>
                    {editingPatient && editingPatient.patientId === patient.patientId ? (
                      <div className="action-buttons">
                        <button className="btn-icon save-btn" onClick={handleSaveEdit}>
                          ✅
                        </button>
                        <button className="btn-icon cancel-btn" onClick={handleCancelEdit}>
                          ❌
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button 
                          className="btn-icon history-btn" 
                          onClick={() => setSelectedPatientForHistory(patient)}
                          title={language === 'de' ? 'Historie anzeigen' : 'View History'}
                        >
                          📊
                        </button>
                        <button className="btn-icon edit-btn" onClick={() => handleEditPatient(patient)}>
                          ✏️
                        </button>
                        <button className="btn-icon delete-btn" onClick={() => handleDeletePatient(patient.patientId)}>
                          🗑️
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPatients.length === 0 && (
          <div className="no-patients">
            <p>{language === 'de' ? 'Keine Patienten gefunden' : 'No patients found'}</p>
          </div>
        )}

        {/* Modal d'historique du patient */}
        {selectedPatientForHistory && (
          <PatientHistoryModal
            patient={selectedPatientForHistory}
            onClose={() => setSelectedPatientForHistory(null)}
            language={language}
          />
        )}

        {/* Modal Excel Preview */}
        {showExcelPreview && (
          <div className="modal-overlay" onClick={() => setShowExcelPreview(false)}>
            <div className="excel-preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📊 {language === 'de' ? 'Excel Vorschau' : 'Excel Preview'}</h2>
                <button 
                  className="modal-close"
                  onClick={() => setShowExcelPreview(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-tabs">
                <button 
                  className={`tab-button ${activeTab === 'summary' ? 'active' : ''}`}
                  onClick={() => setActiveTab('summary')}
                >
                  📋 {language === 'de' ? 'Zusammenfassung' : 'Summary'}
                </button>
                <button 
                  className={`tab-button ${activeTab === 'detailed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('detailed')}
                >
                  📝 {language === 'de' ? 'Detailliert' : 'Detailed'}
                </button>
                <button 
                  className={`tab-button ${activeTab === 'degs' ? 'active' : ''}`}
                  onClick={() => setActiveTab('degs')}
                >
                  🧪 DEGS
                </button>
                <button 
                  className={`tab-button ${activeTab === 'infection' ? 'active' : ''}`}
                  onClick={() => setActiveTab('infection')}
                >
                  🧫 Infection
                </button>
                <button 
                  className={`tab-button ${activeTab === 'promis' ? 'active' : ''}`}
                  onClick={() => setActiveTab('promis')}
                >
                  📋 PROMIS-33
                </button>
                <button 
                  className={`tab-button ${activeTab === 'hoos' ? 'active' : ''}`}
                  onClick={() => setActiveTab('hoos')}
                >
                  🦴 HOOS
                </button>
              </div>
              
              <div className="modal-content">
                <div className="filter-controls">
                  <button 
                    className="clear-filters-btn"
                    onClick={() => clearFilters(activeTab)}
                    disabled={!filters[activeTab] || Object.keys(filters[activeTab]).length === 0}
                  >
                    🗑️ {language === 'de' ? 'Filter löschen' : 'Clear Filters'}
                  </button>
                </div>
                <div className="data-table-container">
                  {activeTab === 'summary' && (
                    <table className="data-table">
                      <thead>
                        <tr>
                          {excelPreviewData.summary[0]?.map((header, index) => (
                            <th key={index}>
                              <div className="column-header">
                                <span>{header}</span>
                                <input
                                  type="text"
                                  placeholder={`Filter ${header}...`}
                                  value={filters.summary[index] || ''}
                                  onChange={(e) => handleFilterChange('summary', index, e.target.value)}
                                  className="column-filter"
                                />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filterData(excelPreviewData.summary, 'summary').slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {typeof cell === 'object' && cell !== null 
                                  ? (cell.value !== undefined ? cell.value : JSON.stringify(cell))
                                  : String(cell || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  
                  {activeTab === 'detailed' && (
                    <table className="data-table">
                      <thead>
                        <tr>
                          {excelPreviewData.detailed[0]?.map((header, index) => (
                            <th key={index}>
                              <div className="column-header">
                                <span>{header}</span>
                                <input
                                  type="text"
                                  placeholder={`Filter ${header}...`}
                                  value={filters.detailed[index] || ''}
                                  onChange={(e) => handleFilterChange('detailed', index, e.target.value)}
                                  className="column-filter"
                                />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filterData(excelPreviewData.detailed, 'detailed').slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {typeof cell === 'object' && cell !== null 
                                  ? (cell.value !== undefined ? cell.value : JSON.stringify(cell))
                                  : String(cell || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  
                  {activeTab === 'degs' && (
                    <table className="data-table">
                      <thead>
                        <tr>
                          {excelPreviewData.degs[0]?.map((header, index) => (
                            <th key={index}>
                              <div className="column-header">
                                <span>{header}</span>
                                <input
                                  type="text"
                                  placeholder={`Filter ${header}...`}
                                  value={filters.degs?.[index] || ''}
                                  onChange={(e) => handleFilterChange('degs', index, e.target.value)}
                                  className="column-filter"
                                />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filterData(excelPreviewData.degs, 'degs').slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {typeof cell === 'object' && cell !== null 
                                  ? (cell.value !== undefined ? cell.value : JSON.stringify(cell))
                                  : String(cell || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  
                  {activeTab === 'infection' && (
                    <table className="data-table">
                      <thead>
                        <tr>
                          {excelPreviewData.infection[0]?.map((header, index) => (
                            <th key={index}>
                              <div className="column-header">
                                <span>{header}</span>
                                <input
                                  type="text"
                                  placeholder={`Filter ${header}...`}
                                  value={filters.infection?.[index] || ''}
                                  onChange={(e) => handleFilterChange('infection', index, e.target.value)}
                                  className="column-filter"
                                />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filterData(excelPreviewData.infection, 'infection').slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {typeof cell === 'object' && cell !== null 
                                  ? (cell.value !== undefined ? cell.value : JSON.stringify(cell))
                                  : String(cell || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {activeTab === 'promis' && (
                    <table className="data-table">
                      <thead>
                        <tr>
                          {excelPreviewData.promis[0]?.map((header, index) => (
                            <th key={index}>
                              <div className="column-header">
                                <span>{header}</span>
                                <input
                                  type="text"
                                  placeholder={`Filter ${header}...`}
                                  value={filters.promis?.[index] || ''}
                                  onChange={(e) => handleFilterChange('promis', index, e.target.value)}
                                  className="column-filter"
                                />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filterData(excelPreviewData.promis, 'promis').slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {typeof cell === 'object' && cell !== null 
                                  ? (cell.value !== undefined ? cell.value : JSON.stringify(cell))
                                  : String(cell || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {activeTab === 'hoos' && (
                    <table className="data-table">
                      <thead>
                        <tr>
                          {excelPreviewData.hoos[0]?.map((header, index) => (
                            <th key={index}>
                              <div className="column-header">
                                <span>{header}</span>
                                <input
                                  type="text"
                                  placeholder={`Filter ${header}...`}
                                  value={filters.hoos?.[index] || ''}
                                  onChange={(e) => handleFilterChange('hoos', index, e.target.value)}
                                  className="column-filter"
                                />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filterData(excelPreviewData.hoos, 'hoos').slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {typeof cell === 'object' && cell !== null 
                                  ? (cell.value !== undefined ? cell.value : JSON.stringify(cell))
                                  : String(cell || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard; 